import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  CalendarIcon,
  ArrowPathIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ScaleIcon,
  UserIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  BuildingOfficeIcon,
  GlobeAsiaAustraliaIcon,
  BuildingStorefrontIcon,
  StarIcon,
  UserGroupIcon,
  BanknotesIcon,
  ChevronUpIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import { format, subDays } from 'date-fns';
import { toZonedTime, fromZonedTime, formatInTimeZone } from 'date-fns-tz';
import { fetchUserTree, getUserData } from '../services/userTreeService';
import { fetchSelf, getSelfUsername } from '../services/userSelf';
import useTranslation from '../services/translationService';
import { useLocation, useNavigate } from 'react-router-dom';
import { isBreakglassModeActive } from '../services/breakglassService';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area
} from 'recharts';
import { apiPost } from '../utils/api';
import { fetchAuthSession } from '@aws-amplify/auth';
import { get } from 'aws-amplify/api';
import { prepareUserIdsPayload, decompressData } from '../utils/compression';
import { tryAggregationsWithFallback } from '../utils/aggregations';

const BEIRUT_TIMEZONE = 'Asia/Beirut';

// Global request queue to ensure only ONE API call happens at a time
let isRequestInProgress = false;
const requestQueue = [];

/**
 * Execute requests sequentially to prevent parallel calls
 */
const executeNextRequest = async () => {
  if (isRequestInProgress || requestQueue.length === 0) {
    return;
  }
  
  isRequestInProgress = true;
  const { resolve, reject, fn } = requestQueue.shift();
  
  try {
    const result = await fn();
    resolve(result);
  } catch (error) {
    reject(error);
  } finally {
    isRequestInProgress = false;
    // Execute next request if any
    executeNextRequest();
  }
};

/**
 * Add request to queue and execute when ready
 */
const queueRequest = (fn) => {
  return new Promise((resolve, reject) => {
    requestQueue.push({ resolve, reject, fn });
    executeNextRequest();
  });
};

/**
 * Split date range into 2-week chunks for parallel fetching
 */
const splitDateRangeIntoChunks = (startDate, endDate, chunkDays = 14) => {
  const chunks = [];
  let currentStart = new Date(startDate);
  const finalEnd = new Date(endDate);
  
  while (currentStart < finalEnd) {
    const currentEnd = new Date(currentStart);
    currentEnd.setDate(currentEnd.getDate() + chunkDays);
    
    // Don't go past the final end date
    if (currentEnd > finalEnd) {
      currentEnd.setTime(finalEnd.getTime());
    }
    
    chunks.push({
      startDate: new Date(currentStart),
      endDate: new Date(currentEnd)
    });
    
    // Move to next chunk (add 1 millisecond to avoid overlap)
    currentStart = new Date(currentEnd.getTime() + 1);
  }
  
  return chunks;
};

/**
 * Consolidated analytics fetch that combines all player IDs into ONE request
 * For date ranges > 14 days, splits into 2-week chunks and fetches in parallel
 */
const fetchCombinedAnalytics = async (allPlayerIds, startDate, endDate, currency, token) => {
  // Remove duplicates
  const uniquePlayerIds = [...new Set(allPlayerIds)];
  
  if (uniquePlayerIds.length === 0) {
    return { data: [], aggregatedByDay: false };
  }
  
  // Calculate date range in days
  const daysDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
  
  // If date range is > 14 days, split into 2-week chunks for parallel fetching
  if (daysDiff > 14) {
    console.log(`[Combined Analytics] Date range is ${daysDiff} days, splitting into 2-week chunks for parallel fetching`);
    
    const chunks = splitDateRangeIntoChunks(startDate, endDate, 14);
    console.log(`[Combined Analytics] Created ${chunks.length} chunks for parallel fetching`);
    
    // Check if we should use server-side fetching for all players
    const COMPRESSION_THRESHOLD = 4000;
    const useServerSideFetch = uniquePlayerIds.length > COMPRESSION_THRESHOLD;
    
    let userIdsPayload;
    if (useServerSideFetch) {
      console.log(`[Combined Analytics] Using server-side fetch for ${uniquePlayerIds.length} players (exceeds threshold)`);
      userIdsPayload = {
        fetchAllPlayers: true,
        playerCount: uniquePlayerIds.length
      };
    } else {
      console.log(`[Combined Analytics] Sending ${uniquePlayerIds.length} player IDs directly (below threshold)`);
      userIdsPayload = {
        userIds: uniquePlayerIds
      };
    }
    
    // Fetch all chunks in parallel
    try {
      const chunkPromises = chunks.map(async (chunk, index) => {
        console.log(`[Chunk ${index + 1}/${chunks.length}] Fetching ${formatDateForAPI(chunk.startDate)} to ${formatDateForAPI(chunk.endDate)}`);
        
        const response = await apiPost(
          'analytics',
          '/analytics',
          {
            ...userIdsPayload,
            startDate: formatDateForAPI(chunk.startDate),
            endDate: formatDateForAPI(chunk.endDate),
            platform: undefined,
            currency: currency,
            aggregate: false
          },
          token
        );
        
        // Handle compressed response
        if (response.compressed && response.data) {
          const decompressedResponse = decompressData(response.data);
          if (decompressedResponse.success && decompressedResponse.data) {
            console.log(`[Chunk ${index + 1}/${chunks.length}] Success: ${decompressedResponse.data.length} records`);
            return decompressedResponse.data;
          }
          throw new Error('Invalid decompressed response');
        } else if (response.success && response.data) {
          console.log(`[Chunk ${index + 1}/${chunks.length}] Success: ${response.data.length} records`);
          return response.data;
        }
        
        throw new Error(response.error || 'Failed to fetch analytics');
      });
      
      const chunkResults = await Promise.all(chunkPromises);
      const combinedData = chunkResults.flat();
      
      console.log(`[Combined Analytics] All chunks fetched successfully. Total records: ${combinedData.length}`);
      
      return {
        data: combinedData,
        aggregatedByDay: false // Chunks are small enough to not be aggregated
      };
      
    } catch (error) {
      console.error(`[Combined Analytics] Chunked request failed:`, error.message);
      throw error;
    }
  }
  
  // For date ranges <= 14 days, use single request (original logic)
  console.log(`[Combined Analytics] Fetching data for ${uniquePlayerIds.length} unique players in ONE request`);
  console.log(`[Combined Analytics] Date range: ${daysDiff} days (${formatDateForAPI(startDate)} to ${formatDateForAPI(endDate)})`);
  
  // Check if we should use server-side fetching for all players
  const COMPRESSION_THRESHOLD = 4000;
  const useServerSideFetch = uniquePlayerIds.length > COMPRESSION_THRESHOLD;
  
  let userIdsPayload;
  if (useServerSideFetch) {
    console.log(`[Combined Analytics] Using server-side fetch for ${uniquePlayerIds.length} players (exceeds threshold)`);
    userIdsPayload = {
      fetchAllPlayers: true,
      playerCount: uniquePlayerIds.length
    };
  } else {
    console.log(`[Combined Analytics] Sending ${uniquePlayerIds.length} player IDs directly (below threshold)`);
    userIdsPayload = {
      userIds: uniquePlayerIds
    };
  }
  
  try {
    const response = await apiPost(
      'analytics',
      '/analytics',
      {
        ...userIdsPayload,
        startDate: formatDateForAPI(startDate),
        endDate: formatDateForAPI(endDate),
        platform: undefined,
        currency: currency,
        aggregate: false
      },
      token
    );
    
    // Handle compressed response
    if (response.compressed && response.data) {
      console.log(`[Combined Analytics] Decompressing response...`);
      const decompressedResponse = decompressData(response.data);
      if (decompressedResponse.success && decompressedResponse.data) {
        console.log(`[Combined Analytics] Success: ${decompressedResponse.data.length} records retrieved`);
        if (decompressedResponse.aggregatedByDay) {
          console.log(`[Combined Analytics] Data pre-aggregated by day on backend (${decompressedResponse.daysDiff} days)`);
        }
        return {
          data: decompressedResponse.data,
          aggregatedByDay: decompressedResponse.aggregatedByDay || false
        };
      }
      throw new Error('Invalid decompressed response');
    } else if (response.success && response.data) {
      console.log(`[Combined Analytics] Success: ${response.data.length} records retrieved`);
      if (response.aggregatedByDay) {
        console.log(`[Combined Analytics] Data pre-aggregated by day on backend (${response.daysDiff} days)`);
      }
      return {
        data: response.data,
        aggregatedByDay: response.aggregatedByDay || false
      };
    }
    
    throw new Error(response.error || 'Failed to fetch analytics');
    
  } catch (error) {
    console.error(`[Combined Analytics] Request failed:`, error.message);
    throw error;
  }
};

/**
 * Helper function to get start of day in Lebanon timezone.
 * Converts a date to midnight (00:00:00) in Asia/Beirut, returned as UTC.
 */
const startOfDayBeirut = (date) => {
  const beirutDate = toZonedTime(date, BEIRUT_TIMEZONE);
  beirutDate.setHours(0, 0, 0, 0);
  return fromZonedTime(beirutDate, BEIRUT_TIMEZONE);
};

/**
 * Helper function to get end of day in Lebanon timezone.
 * Converts a date to 23:59:59.999 in Asia/Beirut, returned as UTC.
 */
const endOfDayBeirut = (date) => {
  const beirutDate = toZonedTime(date, BEIRUT_TIMEZONE);
  beirutDate.setHours(23, 59, 59, 999);
  return fromZonedTime(beirutDate, BEIRUT_TIMEZONE);
};

/**
 * Helper function to get start of month in Lebanon timezone.
 */
const startOfMonthBeirut = (date) => {
  const beirutDate = toZonedTime(date, BEIRUT_TIMEZONE);
  beirutDate.setDate(1);
  beirutDate.setHours(0, 0, 0, 0);
  return fromZonedTime(beirutDate, BEIRUT_TIMEZONE);
};

/**
 * Helper function to get end of month in Lebanon timezone.
 */
const endOfMonthBeirut = (date) => {
  const beirutDate = toZonedTime(date, BEIRUT_TIMEZONE);
  beirutDate.setMonth(beirutDate.getMonth() + 1, 0);
  beirutDate.setHours(23, 59, 59, 999);
  return fromZonedTime(beirutDate, BEIRUT_TIMEZONE);
};

/**
 * ANALYTICS DATA MODEL & INTEGRATION GUIDE
 * ========================================
 * 
 * IMPORTANT: Analytics data (GGR, NGR, Turnover, Balance) is ONLY available at the PLAYER level.
 * 
 * User Hierarchy:
 * - Admin (top level)
 * - Country Admin
 * - City Admin  
 * - Super Agent
 * - Agent
 * - Player (ONLY level with direct analytics data)
 * 
 * DATA AGGREGATION RULES:
 * 
 * 1. PLAYER SELECTION:
 *    - Direct API call with player's userId
 *    - Returns individual analytics data for that player
 *    - Example: /analytics?userIds=['player123']&startDate=2025-01-01&endDate=2025-01-31
 * 
 * 2. AGENT/ADMIN SELECTION (any non-player):
 *    - Must fetch ALL players under the selected agent/admin
 *    - API call includes all child player IDs
 *    - Backend aggregates data from all players
 *    - Example: If selecting a City Admin with 50 players underneath:
 *      /analytics?userIds=['player1','player2',...'player50']&startDate=2025-01-01&endDate=2025-01-31
 * 
 * 3. MIXED SELECTION (agents + players):
 *    - Extract all players from selected agents
 *    - Add directly selected players
 *    - Remove duplicates
 *    - Send combined list to API
 * 
 * API INTEGRATION CHECKLIST:
 * 
 * 1. Replace generateMockAnalyticsData() with actual API call:
 *    ```
 *    const response = await get({
 *      apiName: 'analytics',
 *      path: '/analytics',
 *      options: {
 *        body: {
 *          userIds: playerIds, // ONLY player IDs
 *          startDate: startDate,
 *          endDate: endDate,
 *          platform: platform || 'pm8',
 *          currency: currency,
 *          aggregate: false // Get raw data for client-side processing
 *        }
 *      }
 *    });
 *    ```
 * 
 * 2. Helper function to extract all players under an agent:
 *    - Recursively traverse the tree from selected node
 *    - Collect all nodes where type === 'player'
 *    - Return array of player userIds
 * 
 * 3. Data processing:
 *    - For individual players: display data as-is
 *    - For agents: show aggregated totals from all child players
 *    - Group by date for time series charts
 * 
 * 4. Currency handling:
 *    - Each player may have different currencies
 *    - When aggregating, group by currency
 *    - Show currency selector based on available currencies in selected players
 * 
 * 5. Performance considerations:
 *    - Large agents may have hundreds of players
 *    - Consider pagination or limiting date ranges
 *    - Show loading states during data fetch
 * 
 * 6. Error handling:
 *    - Handle cases where agent has no players
 *    - Handle API timeouts for large data sets
 *    - Show meaningful error messages
 */

// Helper function to get all players under a node
const getAllPlayersUnderNode = (node) => {
  const players = [];
  
  const traverse = (currentNode) => {
    if (!currentNode) return;
    
    // If it's a player, add it
    if (currentNode.type === 'player') {
      players.push({
        username: currentNode.username,
        name: currentNode.name,
        attributes: currentNode.attributes || {},
        balances: currentNode.balances || {}
      });
    }
    
    // Traverse children
    if (currentNode.children && currentNode.children.length > 0) {
      currentNode.children.forEach(child => traverse(child));
    }
  };
  
  traverse(node);
  return players;
};

// Helper function to get display info for selected items
const getSelectionDisplayInfo = (selectedUsers) => {
  let totalPlayers = 0;
  let directPlayers = 0;
  let agents = 0;
  
  selectedUsers.forEach(user => {
    if (user.type === 'player') {
      directPlayers++;
      totalPlayers++;
    } else {
      agents++;
      // Count players under this agent
      const playersUnder = getAllPlayersUnderNode(user);
      totalPlayers += playersUnder.length;
    }
  });
  
  return { totalPlayers, directPlayers, agents };
};

const formatCurrency = (amount, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatNumber = (num) => {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  } else if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  }
  return num.toFixed(0);
};

const DateRangePicker = ({ value, onChange, onCustomDatesChange }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [showCustomInputs, setShowCustomInputs] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Calculate the month name for "2 months ago" in Lebanon time
  const now = new Date();
  const nowInLebanon = toZonedTime(now, BEIRUT_TIMEZONE);
  const twoMonthsAgoDate = new Date(nowInLebanon.getFullYear(), nowInLebanon.getMonth() - 2, 1);
  const twoMonthsAgoMonthName = format(twoMonthsAgoDate, 'MMMM');

  const quickRanges = [
    { label: t('dateRange.today', 'Today'), value: 'today' },
    { label: t('dateRange.yesterday', 'Yesterday'), value: 'yesterday' },
    { label: t('dateRange.last7Days', 'Last 7 Days'), value: 'last7' },
    { label: t('dateRange.last30Days', 'Last 30 Days'), value: 'last30' },
    { label: t('dateRange.thisMonth', 'This Month'), value: 'thisMonth' },
    { label: t('dateRange.lastMonth', 'Last Month'), value: 'lastMonth' },
    { label: `2 Months Ago (${twoMonthsAgoMonthName})`, value: 'twoMonthsAgo' },
    { label: t('dateRange.custom', 'Custom'), value: 'custom' }
  ];

  // Handle custom date selection
  const handleCustomDateChange = () => {
    if (customStartDate && customEndDate) {
      const start = new Date(customStartDate);
      const end = new Date(customEndDate);
      if (start <= end) {
        onCustomDatesChange({ startDate: start, endDate: end });
        setShowCustomInputs(false);
      }
    }
  };

  // Reset custom inputs when switching away from custom
  useEffect(() => {
    if (value !== 'custom') {
      setShowCustomInputs(false);
      setCustomStartDate('');
      setCustomEndDate('');
    }
  }, [value]);

  return (
    <div className="relative w-full">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between gap-2 px-4 py-2.5 text-sm rounded-lg bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors w-full"
      >
        <div className="flex items-center gap-2">
          <CalendarIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          <span className="font-medium">
            {value === 'custom' && customStartDate && customEndDate 
              ? `${formatInTimeZone(new Date(customStartDate), BEIRUT_TIMEZONE, 'MMM d')} - ${formatInTimeZone(new Date(customEndDate), BEIRUT_TIMEZONE, 'MMM d, yyyy')}`
              : quickRanges.find(r => r.value === value)?.label || 'Select Period'
            }
          </span>
        </div>
        <svg className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute top-full mt-2 right-0 z-50 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-2 min-w-[200px]">
            {quickRanges.map((range) => (
              <button
                key={range.value}
                onClick={() => {
                  onChange(range.value);
                  if (range.value === 'custom') {
                    setShowCustomInputs(true);
                  }
                  setIsOpen(false);
                }}
                className={`w-full px-3 py-2 text-sm rounded-lg text-left transition-colors ${
                  value === range.value
                    ? 'bg-primary-500 text-white'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}
              >
                {range.label}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Custom Date Inputs */}
      {showCustomInputs && (
        <div className="absolute top-full mt-2 right-0 z-50 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-4 min-w-[280px]">
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Start Date
              </label>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                max={customEndDate || undefined}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                End Date
              </label>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                min={customStartDate || undefined}
              />
            </div>
            <div className="flex gap-2 pt-2">
              <button
                onClick={handleCustomDateChange}
                disabled={!customStartDate || !customEndDate}
                className="flex-1 px-3 py-2 text-sm bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Apply
              </button>
              <button
                onClick={() => {
                  setShowCustomInputs(false);
                  setCustomStartDate('');
                  setCustomEndDate('');
                }}
                className="px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const UserSelector = ({ selectedUsers, onUsersChange, userTree }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [availableCurrencies, setAvailableCurrencies] = useState(new Set());

  // Auto-expand nodes when searching
  useEffect(() => {
    if (searchQuery.length >= 2) {
      // Expand all nodes that contain matching children
      const newExpanded = new Set();
      
      const expandMatchingNodes = (node, query) => {
        if (!node) return false;
        
        let hasMatch = false;
        
        // Check if current node matches
        if (node.name?.toLowerCase().includes(query.toLowerCase()) ||
            node.username?.toLowerCase().includes(query.toLowerCase())) {
          hasMatch = true;
        }
        
        // Check children
        if (node.children) {
          for (const child of node.children) {
            if (expandMatchingNodes(child, query)) {
              hasMatch = true;
              newExpanded.add(node.username);
            }
          }
        }
        
        return hasMatch;
      };
      
      expandMatchingNodes(userTree, searchQuery);
      setExpandedNodes(newExpanded);
    }
  }, [searchQuery, userTree]);

  // Update available currencies when users are selected
  useEffect(() => {
    const currencies = new Set();
    selectedUsers.forEach(user => {
      if (user.attributes?.currencies) {
        user.attributes.currencies.forEach(curr => currencies.add(curr));
      }
    });
    setAvailableCurrencies(currencies);
  }, [selectedUsers]);

  const getUserIcon = (type) => {
    switch (type) {
      case 'admin': return BuildingOfficeIcon;
      case 'country': return GlobeAsiaAustraliaIcon;
      case 'city': return BuildingStorefrontIcon;
      case 'super-agent': return StarIcon;
      case 'agent': return UserGroupIcon;
      default: return UserIcon;
    }
  };

  const getUserStyle = (type) => {
    switch (type) {
      case 'admin': return 'text-purple-500';
      case 'country': return 'text-blue-500';
      case 'city': return 'text-green-500';
      case 'super-agent': return 'text-orange-500';
      case 'agent': return 'text-yellow-500';
      default: return 'text-gray-400';
    }
  };

  const toggleNode = (nodeId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const toggleUser = (user) => {
    const index = selectedUsers.findIndex(u => u.username === user.username);
    let newSelected;
    
    if (index > -1) {
      // If user is already selected, deselect them
      newSelected = [];
    } else {
      // If user is not selected, select only this user (deselect others)
      newSelected = [{
        ...user,
        attributes: user.attributes || {},
        balances: user.balances || {}
      }];
    }
    
    onUsersChange(newSelected);
    
    // Auto-close the popup when a user is selected
    setIsOpen(false);
  };

  const filterUsers = (node, query) => {
    if (!node) return false;
    const matches = node.name?.toLowerCase().includes(query.toLowerCase()) ||
                   node.username?.toLowerCase().includes(query.toLowerCase());
    
    let childMatches = false;
    if (node.children) {
      childMatches = node.children.some(child => filterUsers(child, query));
    }
    
    return matches || childMatches;
  };

  // Helper function to collect all matching nodes when searching
  const collectSearchResults = (node, query) => {
    const results = [];
    const queryLower = query.toLowerCase();
    
    const traverse = (currentNode) => {
      if (!currentNode) return;
      
      // Check if current node matches
      if (currentNode.name?.toLowerCase().includes(queryLower) ||
          currentNode.username?.toLowerCase().includes(queryLower)) {
        results.push(currentNode);
      }
      
      // Check children
      if (currentNode.children) {
        currentNode.children.forEach(child => traverse(child));
      }
    };
    
    traverse(node);
    
    // Sort by username length (shortest first)
    results.sort((a, b) => {
      const usernameA = a.username || '';
      const usernameB = b.username || '';
      return usernameA.length - usernameB.length;
    });
    
    return results;
  };

  const renderUserNode = (node, level = 0, isSearchMode = false) => {
    if (!node) return null;
    
    // Apply search filter only if not in search mode (to avoid double filtering)
    if (!isSearchMode && searchQuery.length >= 2 && !filterUsers(node, searchQuery)) {
      return null;
    }

    const isExpanded = expandedNodes.has(node.username);
    const isSelected = selectedUsers.some(u => u.username === node.username);
    const hasChildren = node.children && node.children.length > 0;
    const Icon = getUserIcon(node.type);

    // Get user currencies
    const userCurrencies = node.attributes?.currencies || [];
    const currencyDisplay = userCurrencies.length > 0 
      ? userCurrencies.join(', ') 
      : 'No currencies';

    return (
      <div key={node.username} className="select-none">
        <div 
          className={`flex items-center gap-2 px-2 py-1.5 rounded-lg transition-colors ${
            isSelected 
              ? 'bg-primary-100 dark:bg-primary-900/30 border border-primary-300 dark:border-primary-700' 
              : 'hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
          style={{ paddingLeft: `${level * 20 + 8}px` }}
        >
          {hasChildren && !isSearchMode && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleNode(node.username);
              }}
              className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
            >
              {isExpanded ? (
                <ChevronDownIcon className="w-4 h-4 text-gray-400" />
              ) : (
                <ChevronRightIcon className="w-4 h-4 text-gray-400" />
              )}
            </button>
          )}
          {(!hasChildren || isSearchMode) && <div className="w-5" />}
          
          <div
            onClick={() => toggleUser(node)}
            className="flex items-center gap-2 flex-1 cursor-pointer"
          >
            <Icon className={`w-4 h-4 ${getUserStyle(node.type)}`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                  {node.name}
                </span>
                {isSelected && (
                  <span className="text-xs px-1.5 py-0.5 bg-primary-500 text-white rounded-full">
                    Selected
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                <span>@{node.username}</span>
                <span>•</span>
                <span>{currencyDisplay}</span>
              </div>
            </div>
          </div>
        </div>
        
        {hasChildren && isExpanded && !isSearchMode && (
          <div>
            {node.children.map(child => renderUserNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  // Get unique currencies from selected users
  const selectedCurrencies = Array.from(availableCurrencies);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors w-full"
      >
        <UserIcon className="w-4 h-4" />
        <span className="flex-1 text-left">
          {selectedUsers.length === 0 
            ? 'Select User' 
            : (
              <div className="flex flex-col">
                <span>{selectedUsers[0].name || selectedUsers[0].username}</span>
                {selectedCurrencies.length > 0 && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Currencies: {selectedCurrencies.join(', ')}
                  </span>
                )}
              </div>
            )}
        </span>
      </button>

      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute top-full mt-2 left-0 right-0 z-50 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 max-h-[400px] overflow-hidden flex flex-col">
            <div className="p-3 border-b border-gray-200 dark:border-gray-700">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search users (min 2 characters)..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {selectedUsers.length > 0 && '1 user selected'}
                </span>
                {selectedUsers.length > 0 && (
                  <button
                    onClick={() => onUsersChange([])}
                    className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    Clear selection
                  </button>
                )}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {searchQuery.length >= 2 ? (
                // Show sorted search results
                collectSearchResults(userTree, searchQuery).map(node => 
                  renderUserNode(node, 0, true)
                )
              ) : userTree ? (
                // Show full tree when not searching
                renderUserNode(userTree)
              ) : (
                <div className="text-center py-4 text-sm text-gray-500 dark:text-gray-400">
                  {searchQuery.length < 2 && searchQuery.length > 0 
                    ? 'Type at least 2 characters to search' 
                    : 'Loading users...'}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Helper function to format date for API
// IMPORTANT: Backend now accepts full ISO strings with time
// We send the exact UTC timestamp that represents the start/end of day in Beirut
const formatDateForAPI = (date) => {
  // Send full ISO string - backend will use the exact timestamp
  // The date passed here is already adjusted for Beirut timezone by startOfDayBeirut/endOfDayBeirut
  return date.toISOString();
};

// Helper function to parse date_hour from API
const parseDateHour = (dateHour) => {
  // dateHour format: "YYYY-MM-DD-HH" where HH is in UTC
  const [date, hour] = dateHour.split('-').reduce((acc, part, index) => {
    if (index < 3) {
      acc[0] = acc[0] ? `${acc[0]}-${part}` : part;
    } else {
      acc[1] = part;
    }
    return acc;
  }, ['', '']);
  
  // Parse as UTC - the date_hour from backend is already in UTC
  const utcDate = new Date(`${date}T${hour.padStart(2, '0')}:00:00Z`);
  
  // Return the UTC date directly - formatInTimeZone will handle timezone display
  return utcDate;
};

// Helper function to aggregate analytics data by date or hour
const aggregateByDate = (analyticsData, isHourly = false, isPreAggregated = false) => {
  // If data is pre-aggregated by day from backend, just format it
  if (isPreAggregated && !isHourly) {
    console.log('[Aggregate] Using pre-aggregated daily data from backend');
    return analyticsData.map(record => {
      const date = record.date || record.date_hour.substring(0, 10); // Extract YYYY-MM-DD
      const dateObj = new Date(date + 'T00:00:00Z');
      
      return {
        date: date,
        dateObj: dateObj,
        ggr: parseFloat(record.ggr || 0),
        ngr: parseFloat(record.ngr || 0),
        turnover: parseFloat(record.turnover || 0),
        balance: parseFloat(record.last_bet_balance || 0),
        recordCount: 1,
        platforms: [],
        // Platform-specific data (not available in pre-aggregated)
        casinoGGR: 0,
        casinoNGR: 0,
        casinoTurnover: 0,
        sportGGR: 0,
        sportNGR: 0,
        sportTurnover: 0
      };
    }).sort((a, b) => a.dateObj.getTime() - b.dateObj.getTime());
  }
  
  const dataMap = new Map();
  const platformDataMap = new Map(); // Track platform-specific data
  
  analyticsData.forEach(record => {
    // Skip records without date_hour
    if (!record.date_hour) return;
    
    const date = parseDateHour(record.date_hour);
    let key;
    
    if (isHourly) {
      key = formatInTimeZone(date, BEIRUT_TIMEZONE, 'yyyy-MM-dd HH:00');
    } else {
      key = formatInTimeZone(date, BEIRUT_TIMEZONE, 'yyyy-MM-dd');
    }
    
    if (!dataMap.has(key)) {
      dataMap.set(key, {
        date: key,
        dateObj: date, // Store the actual Date object for chart formatting
        ggr: 0,
        ngr: 0,
        turnover: 0,
        balance: 0,
        recordCount: 0,
        latestHour: -1,
        platforms: new Set(),
        // Platform-specific data
        casinoGGR: 0,
        casinoNGR: 0,
        casinoTurnover: 0,
        sportGGR: 0,
        sportNGR: 0,
        sportTurnover: 0
      });
    }
    
    const data = dataMap.get(key);
    const ggr = parseFloat(record.ggr || 0);
    const ngr = parseFloat(record.ngr || 0);
    const turnover = parseFloat(record.turnover || 0);
    
    data.ggr += ggr;
    data.ngr += ngr;
    data.turnover += turnover;
    
    // Track platforms and platform-specific data
    if (record.platform) {
      data.platforms.add(record.platform);
      
      if (record.platform === 'pm8') {
        data.casinoGGR += ggr;
        data.casinoNGR += ngr;
        data.casinoTurnover += turnover;
      } else if (record.platform === 'sport') {
        data.sportGGR += ggr;
        data.sportNGR += ngr;
        data.sportTurnover += turnover;
      }
    }
    
    // For balance, use the latest hour's balance
    const hour = parseInt(record.date_hour.split('-')[3] || 0);
    if (isHourly || hour > data.latestHour) {
      data.balance = parseFloat(record.last_bet_balance || 0);
      data.latestHour = hour;
    }
    
    data.recordCount += 1;
  });
  
  // Convert to array, process platforms, and sort
  return Array.from(dataMap.values())
    .map(({ latestHour, platforms, ...rest }) => ({
      ...rest,
      platforms: Array.from(platforms)
    }))
    .sort((a, b) => {
      // Sort by dateObj if available, otherwise parse date string
      const aTime = a.dateObj ? a.dateObj.getTime() : new Date(a.date).getTime();
      const bTime = b.dateObj ? b.dateObj.getTime() : new Date(b.date).getTime();
      return aTime - bTime;
    });
};

// Helper function to check if transaction is from TurboStars
const isTurboStarsTransaction = (tx) => {
  return tx.context?.provider?.toLowerCase() === 'turbostars' ||
         tx.metadata?.provider?.toLowerCase() === 'turbostars' ||
         tx.transactionId?.includes('-settle-') ||
         tx.transactionId?.includes('-rollback-');
};

// Helper function to get base transaction ID (remove -settle- or -rollback- suffixes)
const getBaseTransactionId = (transactionId) => {
  if (!transactionId) return transactionId;
  const settleMatch = transactionId.match(/^(.+?)-settle-/);
  const rollbackMatch = transactionId.match(/^(.+?)-rollback-/);
  if (settleMatch) return settleMatch[1];
  if (rollbackMatch) return rollbackMatch[1];
  return transactionId;
};

// Helper to get transaction timestamp
const getTransactionTimestamp = (tx) => {
  return tx.timestamp || tx.createdAt || 0;
};

// Group game transactions by round/bet ID
const groupGameTransactionsByRound = (transactions) => {
  const rounds = new Map();
  const standaloneTransactions = [];
  
  // Group by roundId (casino) or betId/correlationId (sports)
  transactions.forEach(tx => {
    const isSportsBet = tx.platform?.toLowerCase() === 'sport' || 
                        tx.platform?.toLowerCase() === 'sports' || 
                        tx.context?.product?.toLowerCase() === 'sportsbook';
    
    let groupingId = null;
    
    if (isSportsBet) {
      if (isTurboStarsTransaction(tx)) {
        groupingId = tx.context?.parentId || 
                     tx.context?.additionalContext?.originalBetTransactionId ||
                     getBaseTransactionId(tx.transactionId);
      } else {
        groupingId = tx.context?.betId || tx.context?.correlationId;
      }
    } else {
      groupingId = tx.context?.roundId;
    }
    
    if (groupingId) {
      if (!rounds.has(groupingId)) {
        rounds.set(groupingId, []);
      }
      rounds.get(groupingId).push(tx);
    } else {
      standaloneTransactions.push([tx]);
    }
  });
  
  return [...Array.from(rounds.values()), ...standaloneTransactions];
};

// Calculate GGR for a single round/bet
const calculateRoundGGR = (transactions) => {
  const sorted = [...transactions].sort((a, b) => 
    getTransactionTimestamp(a) - getTransactionTimestamp(b)
  );
  
  const firstTx = sorted[0];
  const isTurboStars = transactions.some(tx => isTurboStarsTransaction(tx));
  const isSportsBet = firstTx.platform?.toLowerCase() === 'sport' || 
                      firstTx.platform?.toLowerCase() === 'sports' || 
                      firstTx.context?.product?.toLowerCase() === 'sportsbook';
  
  // Categorize transactions
  const bets = transactions.filter(tx => {
    if (isTurboStars) {
      return tx.type?.toLowerCase() === 'withdrawal' && 
             !tx.transactionId?.includes('-settle-') &&
             !tx.transactionId?.includes('-rollback-');
    }
    return tx.type?.toLowerCase() === 'withdrawal' && 
           (tx.context?.reason?.toLowerCase() === 'bet' || !tx.context?.reason);
  });
  
  const wins = transactions.filter(tx => 
    tx.type?.toLowerCase() === 'deposit' && !tx.context?.reason
  );
  
  const settles = transactions.filter(tx => 
    tx.type?.toLowerCase() === 'deposit' && tx.context?.reason?.toLowerCase() === 'settle'
  );
  
  const resettles = transactions.filter(tx => 
    (tx.type?.toLowerCase() === 'deposit' || tx.type?.toLowerCase() === 'withdrawal') && 
    tx.context?.reason?.toLowerCase() === 'resettle'
  );
  
  const cancelSettles = transactions.filter(tx => 
    tx.type?.toLowerCase() === 'withdrawal' && tx.context?.reason?.toLowerCase() === 'cancelsettle'
  );
  
  const rollbacks = transactions.filter(tx => 
    tx.type?.toLowerCase() === 'rollback' ||
    tx.transactionId?.includes('-rollback-')
  );
  
  const hasRollback = rollbacks.length > 0 || 
                      bets.some(b => b.rolledBack === 'true' || b.rolledBack === true);
  
  const totalBet = hasRollback ? 0 : bets.reduce((sum, tx) => sum + parseFloat(tx.amount || 0), 0);
  const totalWin = wins.reduce((sum, tx) => sum + parseFloat(tx.amount || 0), 0);
  const totalSettle = settles.reduce((sum, tx) => sum + parseFloat(tx.amount || 0), 0);
  const totalResettle = resettles.reduce((sum, tx) => {
    const amount = parseFloat(tx.amount || 0);
    return sum + (tx.type?.toLowerCase() === 'deposit' ? amount : -amount);
  }, 0);
  const totalCancelSettle = cancelSettles.reduce((sum, tx) => sum - parseFloat(tx.amount || 0), 0);
  
  const netResult = hasRollback ? 0 : totalWin + totalSettle + totalResettle + totalCancelSettle - totalBet;
  const roundGGR = hasRollback ? 0 : totalBet - (totalWin + totalSettle + totalResettle + totalCancelSettle);
  
  // Get grouping ID
  let groupingId = null;
  if (isSportsBet) {
    if (isTurboStars) {
      groupingId = firstTx.context?.parentId || 
                   firstTx.context?.additionalContext?.originalBetTransactionId ||
                   getBaseTransactionId(firstTx.transactionId);
    } else {
      groupingId = firstTx.context?.betId || firstTx.context?.correlationId;
    }
  } else {
    groupingId = firstTx.context?.roundId;
  }
  
  return {
    roundId: groupingId || firstTx.transactionId,
    transactions: sorted,
    isSportsBet,
    isCasino: !isSportsBet,
    isTurboStars,
    totalBet,
    totalWin,
    totalSettle,
    totalResettle,
    totalCancelSettle,
    netResult,
    roundGGR,
    hasRollback,
    hasBet: bets.length > 0,
    hasSettlement: settles.length > 0 || resettles.length > 0 || wins.length > 0,
    currency: firstTx.currency,
    platform: firstTx.platform,
    timestamp: getTransactionTimestamp(firstTx),
    betCount: bets.length,
    settleCount: settles.length,
    resettleCount: resettles.length,
    rollbackCount: rollbacks.length
  };
};

// Identify issues with a round/bet
const identifyRoundIssues = (round, startDate, endDate) => {
  const issues = [];
  const warnings = [];
  const info = [];
  
  // 1. Missing Settlement
  if (round.hasBet && !round.hasSettlement && !round.hasRollback) {
    issues.push({
      type: 'missing_settlement',
      severity: 'critical',
      message: 'Bet placed but no settlement found',
      impact: 'GGR should be 0 until settled'
    });
  }
  
  // 2. Settlement without Bet
  if (round.hasSettlement && !round.hasBet) {
    issues.push({
      type: 'orphaned_settlement',
      severity: 'critical',
      message: 'Settlement exists without corresponding bet',
      impact: 'Invalid transaction pattern'
    });
  }
  
  // 3. Multiple Resettlements
  if (round.resettleCount > 1) {
    warnings.push({
      type: 'multiple_resettles',
      severity: 'warning',
      message: `${round.resettleCount} resettlements detected`,
      impact: 'May indicate bet adjustment issues'
    });
  }
  
  // 4. Rolled Back Bet
  if (round.hasRollback) {
    info.push({
      type: 'rollback',
      severity: 'info',
      message: 'Bet was cancelled/refunded',
      impact: 'GGR = 0 (correct)'
    });
  }
  
  // 5. Large Player Win (negative GGR)
  if (round.roundGGR < 0 && Math.abs(round.roundGGR) > 100) {
    info.push({
      type: 'large_win',
      severity: 'info',
      message: `Player won ${Math.abs(round.roundGGR).toFixed(2)} ${round.currency}`,
      impact: 'Negative GGR contribution'
    });
  }
  
  // 6. Large Bet Amount
  if (round.totalBet > 1000) {
    info.push({
      type: 'large_bet',
      severity: 'info',
      message: `Large bet amount: ${round.totalBet.toFixed(2)} ${round.currency}`,
      impact: 'High impact on total GGR'
    });
  }
  
  // 7. Timing Issues (transaction outside date range)
  const txTimestamp = round.timestamp;
  if (txTimestamp < startDate.getTime() || txTimestamp > endDate.getTime()) {
    issues.push({
      type: 'out_of_range',
      severity: 'critical',
      message: 'Transaction outside selected date range',
      impact: 'Should not be included in GGR calculation'
    });
  }
  
  // 8. Duplicate Bets
  if (round.betCount > 1 && !round.isCasino) {
    warnings.push({
      type: 'multiple_bets',
      severity: 'warning',
      message: `${round.betCount} bet transactions in same group`,
      impact: 'Possible duplicate bet'
    });
  }
  
  return { issues, warnings, info };
};

/**
 * Helper function to calculate date range based on selection.
 * All boundaries are computed in Lebanon time (Asia/Beirut) then converted to UTC.
 */
const getDateRange = (dateRangeSelection, customDates = {}) => {
  const now = new Date();
  let endDate, startDate;
  
  switch (dateRangeSelection) {
    case 'today':
      startDate = startOfDayBeirut(now);
      endDate = endOfDayBeirut(now);
      break;
    case 'yesterday': {
      const yesterday = subDays(now, 1);
      startDate = startOfDayBeirut(yesterday);
      endDate = endOfDayBeirut(yesterday);
      break;
    }
    case 'last7': {
      const sevenDaysAgo = subDays(now, 7);
      startDate = startOfDayBeirut(sevenDaysAgo);
      endDate = endOfDayBeirut(now);
      break;
    }
    case 'last30': {
      const thirtyDaysAgo = subDays(now, 30);
      startDate = startOfDayBeirut(thirtyDaysAgo);
      endDate = endOfDayBeirut(now);
      break;
    }
    case 'thisMonth':
      startDate = startOfMonthBeirut(now);
      endDate = endOfDayBeirut(now);
      break;
    case 'lastMonth': {
      const nowInLebanon = toZonedTime(now, BEIRUT_TIMEZONE);
      const lastMonthDate = new Date(nowInLebanon.getFullYear(), nowInLebanon.getMonth() - 1, 15);
      startDate = startOfMonthBeirut(fromZonedTime(lastMonthDate, BEIRUT_TIMEZONE));
      endDate = endOfMonthBeirut(fromZonedTime(lastMonthDate, BEIRUT_TIMEZONE));
      break;
    }
    case 'twoMonthsAgo': {
      const nowInLebanon = toZonedTime(now, BEIRUT_TIMEZONE);
      const twoMonthsAgoRef = new Date(nowInLebanon.getFullYear(), nowInLebanon.getMonth() - 2, 15);
      startDate = startOfMonthBeirut(fromZonedTime(twoMonthsAgoRef, BEIRUT_TIMEZONE));
      endDate = endOfMonthBeirut(fromZonedTime(twoMonthsAgoRef, BEIRUT_TIMEZONE));
      break;
    }
    case 'custom':
      if (customDates.startDate && customDates.endDate) {
        startDate = startOfDayBeirut(customDates.startDate);
        endDate = endOfDayBeirut(customDates.endDate);
      } else {
        const defaultThirtyDaysAgo = subDays(now, 30);
        startDate = startOfDayBeirut(defaultThirtyDaysAgo);
        endDate = endOfDayBeirut(now);
      }
      break;
    default: {
      const defaultThirtyDaysAgo = subDays(now, 30);
      startDate = startOfDayBeirut(defaultThirtyDaysAgo);
      endDate = endOfDayBeirut(now);
    }
  }
  
  return { startDate, endDate };
};

const Reports = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('thisMonth');
  const [customDates, setCustomDates] = useState({ startDate: null, endDate: null });
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userTree, setUserTree] = useState(null);
  const [analyticsData, setAnalyticsData] = useState([]);
  const [currency, setCurrency] = useState('USD');
  const [availableCurrencies, setAvailableCurrencies] = useState(['USD']);
  const [effectivePlayers, setEffectivePlayers] = useState([]); // All players whose data will be fetched
  const [initialCurrencySet, setInitialCurrencySet] = useState(false); // Track if initial currency has been set
  const [directReportsData, setDirectReportsData] = useState([]); // Analytics for direct reports
  const [loadingDirectReports, setLoadingDirectReports] = useState(false);
  const [expandedReports, setExpandedReports] = useState(new Set()); // Track which reports are expanded
  const [expandedMetrics, setExpandedMetrics] = useState(false); // Track expanded metrics section
  const [selectedHeroMetric, setSelectedHeroMetric] = useState('ggr'); // 'ggr', 'ngr', 'turnover', 'balance'
  const [showCasinoMetrics, setShowCasinoMetrics] = useState(false); // Track if casino metrics are shown on chart
  const [showSportMetrics, setShowSportMetrics] = useState(false); // Track if sport metrics are shown on chart
  const [isRefreshing, setIsRefreshing] = useState(false); // Track refresh state
  const [showCumulative, setShowCumulative] = useState(true); // Track cumulative view
  const [directPlayerAnalytics, setDirectPlayerAnalytics] = useState(null); // Analytics for direct players only
  const [loadingDirectPlayers, setLoadingDirectPlayers] = useState(false);
  const [showZeroGGRReports, setShowZeroGGRReports] = useState(false); // Track whether to show zero GGR reports
  const [isInitialized, setIsInitialized] = useState(false); // Track if initial setup is complete
  const [lastFetchParams, setLastFetchParams] = useState(null); // Track last fetch parameters to prevent duplicate calls
  const currentRequestIdRef = useRef(0); // Track request ID to prevent race conditions (using ref for immediate updates)
  const [isBreakglassActive, setIsBreakglassActive] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false); // Track if user is Admin or playwinadmin
  const [currentUsername, setCurrentUsername] = useState('');
  const [showGGRAnalysisModal, setShowGGRAnalysisModal] = useState(false);
  const [ggrAnalysisLoading, setGGRAnalysisLoading] = useState(false);
  const [ggrAnalysisResults, setGGRAnalysisResults] = useState(null);
  const [expandedPlayers, setExpandedPlayers] = useState(new Set()); // Track which players are expanded
  const [deepDiveResults, setDeepDiveResults] = useState(null);
  const [deepDiveLoading, setDeepDiveLoading] = useState(false);
  const [showDeepDiveModal, setShowDeepDiveModal] = useState(false);
  const [deepDivePlayer, setDeepDivePlayer] = useState(null);
  const [expandedRounds, setExpandedRounds] = useState(new Set()); // Track which rounds are expanded in deep dive

  // Check for breakglass mode and admin permissions
  useEffect(() => {
    const checkPermissions = async () => {
      const breakglassActive = await isBreakglassModeActive();
      setIsBreakglassActive(breakglassActive);
      
      // Check if user is admin or playwinadmin
      try {
        const session = await fetchAuthSession();
        const userGroups = session.tokens?.accessToken?.payload['cognito:groups'] || [];
        const username = await getSelfUsername();
        
        const hasAdminAccess = userGroups.includes('Admins') || username === 'playwinadmin';
        setIsAdmin(hasAdminAccess);
        setCurrentUsername(username);
        
        console.log('Admin access check:', { hasAdminAccess, username, userGroups });
      } catch (error) {
        console.error('Error checking admin permissions:', error);
        setIsAdmin(false);
      }
    };
    checkPermissions();
  }, []);

  // Fetch user tree on mount and handle auto-selection
  useEffect(() => {
    const loadUserTree = async () => {
      try {
        const treeData = await fetchUserTree(false, { staleWhileRevalidate: true });
        console.log('User tree data:', treeData);
        setUserTree(treeData);
        
        // Check if a user was passed from navigation state
        if (location.state?.selectedUser) {
          console.log('User selected from navigation:', location.state.selectedUser);
          const navUser = location.state.selectedUser;
          
          // IMPORTANT: Find the user in the actual tree structure to get complete data with children
          const findUserInTree = (node, username) => {
            if (!node) return null;
            if (node.username === username) return node;
            if (node.children) {
              for (const child of node.children) {
                const found = findUserInTree(child, username);
                if (found) return found;
              }
            }
            return null;
          };
          
          // Use getUserData service or find in tree to get complete user node with children
          const userInTree = findUserInTree(treeData, navUser.username);
          
          if (userInTree) {
            console.log('Found user in tree with complete structure:', userInTree);
            setSelectedUsers([userInTree]);
            
            // Set initial currency based on selected user's currencies
            if (!initialCurrencySet && userInTree.attributes?.currencies) {
              const userCurrencies = userInTree.attributes.currencies;
              if (userCurrencies.includes('LBP')) {
                setCurrency('LBP');
              } else if (userCurrencies.includes('USD')) {
                setCurrency('USD');
              } else if (userCurrencies.length > 0) {
                setCurrency(userCurrencies[0]);
              }
              setInitialCurrencySet(true);
            }
          } else {
            // Fallback: if user not found in tree, use the passed data but log a warning
            console.warn('User not found in tree, using passed data (may have incomplete structure):', navUser.username);
            setSelectedUsers([navUser]);
            
            // Set initial currency based on selected user's currencies
            if (!initialCurrencySet && navUser.attributes?.currencies) {
              const userCurrencies = navUser.attributes.currencies;
              if (userCurrencies.includes('LBP')) {
                setCurrency('LBP');
              } else if (userCurrencies.includes('USD')) {
                setCurrency('USD');
              } else if (userCurrencies.length > 0) {
                setCurrency(userCurrencies[0]);
              }
              setInitialCurrencySet(true);
            }
          }
          
          // Don't clear the state here - it causes re-render and falls back to auto-select
          // The state will be cleared on next navigation anyway
        } else if (selectedUsers.length === 0) {
          // Only auto-select current user if no user is already selected
          // Auto-select current user
          try {
            // Check if treeData has a main property (current user)
            if (treeData && treeData.main) {
              const currentUser = treeData.main;
              console.log('Found current user in treeData.main:', currentUser);
              
              setSelectedUsers([{
                ...currentUser,
                attributes: currentUser.attributes || {},
                balances: currentUser.balances || {}
              }]);
              console.log('Auto-selected current user:', currentUser.username);
              
              // Set initial currency based on current user's currencies
              if (!initialCurrencySet && currentUser.attributes?.currencies) {
                const userCurrencies = currentUser.attributes.currencies;
                if (userCurrencies.includes('LBP')) {
                  setCurrency('LBP');
                } else if (userCurrencies.includes('USD')) {
                  setCurrency('USD');
                } else if (userCurrencies.length > 0) {
                  setCurrency(userCurrencies[0]);
                }
                setInitialCurrencySet(true);
              }
            } else if (treeData && treeData.username) {
              // If no main property, the treeData itself might be the current user
              console.log('Using treeData as current user:', treeData);
              
              setSelectedUsers([{
                ...treeData,
                attributes: treeData.attributes || {},
                balances: treeData.balances || {}
              }]);
              console.log('Auto-selected current user:', treeData.username);
              
              // Set initial currency based on current user's currencies
              if (!initialCurrencySet && treeData.attributes?.currencies) {
                const userCurrencies = treeData.attributes.currencies;
                if (userCurrencies.includes('LBP')) {
                  setCurrency('LBP');
                } else if (userCurrencies.includes('USD')) {
                  setCurrency('USD');
                } else if (userCurrencies.length > 0) {
                  setCurrency(userCurrencies[0]);
                }
                setInitialCurrencySet(true);
              }
            } else {
              // Fallback: try to get current user from userSelf service
              const currentUsername = await getSelfUsername();
              console.log('Current username from userSelf:', currentUsername);
              
              if (currentUsername && treeData) {
                // Find current user in the tree
                const findUserInTree = (node) => {
                  if (node.username === currentUsername) {
                    return node;
                  }
                  if (node.children) {
                    for (const child of node.children) {
                      const found = findUserInTree(child);
                      if (found) return found;
                    }
                  }
                  return null;
                };
                
                const currentUser = findUserInTree(treeData);
                console.log('Found current user in tree:', currentUser);
                
                if (currentUser) {
                  setSelectedUsers([{
                    ...currentUser,
                    attributes: currentUser.attributes || {},
                    balances: currentUser.balances || {}
                  }]);
                  console.log('Auto-selected current user:', currentUser.username);
                  
                  // Set initial currency based on current user's currencies
                  if (!initialCurrencySet && currentUser.attributes?.currencies) {
                    const userCurrencies = currentUser.attributes.currencies;
                    if (userCurrencies.includes('LBP')) {
                      setCurrency('LBP');
                    } else if (userCurrencies.includes('USD')) {
                      setCurrency('USD');
                    } else if (userCurrencies.length > 0) {
                      setCurrency(userCurrencies[0]);
                    }
                    setInitialCurrencySet(true);
                  }
                } else {
                  console.log('Current user not found in tree');
                }
              } else {
                console.log('No current username or tree data available');
              }
            }
          } catch (error) {
            console.error('Error auto-selecting current user:', error);
          }
        }
      } catch (error) {
        console.error('Error fetching user tree:', error);
      } finally {
        // Mark initialization as complete
        setIsInitialized(true);
      }
    };
    loadUserTree();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, initialCurrencySet]);

  // Calculate effective players whenever selection changes
  useEffect(() => {
    const playersSet = new Set();
    const currencies = new Set();
    
    selectedUsers.forEach(user => {
      if (user.type === 'player') {
        // Direct player selection
        playersSet.add(user.username);
        if (user.attributes?.currencies) {
          user.attributes.currencies.forEach(curr => currencies.add(curr));
        }
      } else {
        // Agent/Admin selection - get all players underneath
        const playersUnder = getAllPlayersUnderNode(user);
        playersUnder.forEach(player => {
          playersSet.add(player.username);
          if (player.attributes?.currencies) {
            player.attributes.currencies.forEach(curr => currencies.add(curr));
          }
        });
      }
    });
    
    // If no currencies found, default to USD
    if (currencies.size === 0) {
      currencies.add('USD');
    }
    
    setEffectivePlayers(Array.from(playersSet));
    setAvailableCurrencies(Array.from(currencies));
    
    // If current currency is not available, switch to first available
    if (!currencies.has(currency)) {
      setCurrency(Array.from(currencies)[0]);
    }
    
    // Clear old data immediately when selection changes to prevent stale data display
    // The new data will be fetched by the main analytics useEffect
    setAnalyticsData([]);
    setDirectReportsData([]);
    setDirectPlayerAnalytics(null);
  }, [selectedUsers, currency]);

  // Get direct reports of selected users
  const getDirectReports = () => {
    const directReports = [];
    
    selectedUsers.forEach(user => {
      if (user.children && user.children.length > 0) {
        user.children.forEach(child => {
          // Check if this child is already in the list
          if (!directReports.find(r => r.username === child.username)) {
            directReports.push({
              ...child,
              parentUsername: user.username,
              parentName: user.name
            });
          }
        });
      }
    });
    
    return directReports;
  };

  // Get only direct players (not agents) under selected super-agent
  const getDirectPlayers = () => {
    if (selectedUsers.length !== 1 || selectedUsers[0].type !== 'super-agent') {
      return [];
    }
    
    const directPlayers = [];
    const superAgent = selectedUsers[0];
    
    if (superAgent.children && superAgent.children.length > 0) {
      superAgent.children.forEach(child => {
        if (child.type === 'player') {
          directPlayers.push({
            username: child.username,
            name: child.name,
            attributes: child.attributes || {},
            balances: child.balances || {}
          });
        }
      });
    }
    
    return directPlayers;
  };
  
  // Fetch all analytics data when dependencies change
  // OPTIMIZATION: Makes ONE single API call with all player IDs combined
  // instead of multiple parallel calls that could fail and create many batch requests
  useEffect(() => {
    // Skip if not initialized yet
    if (!isInitialized) {
      console.log('[Analytics] Skipping fetch - component not initialized yet');
      return;
    }
    
    const fetchAllAnalyticsInOneCall = async () => {
      // Increment request ID to invalidate any previous in-flight requests
      currentRequestIdRef.current = currentRequestIdRef.current + 1;
      const requestId = currentRequestIdRef.current;
      console.log(`[Analytics] Starting new request #${requestId}`);
      
      // Skip if no players to fetch
      if (effectivePlayers.length === 0 && selectedUsers.length === 0) {
        setAnalyticsData([]);
        setDirectReportsData([]);
        setDirectPlayerAnalytics(null);
        setLastFetchParams(null);
        return;
      }
      
      // Create a unique key for the current fetch parameters
      const currentFetchParams = JSON.stringify({
        effectivePlayers: effectivePlayers.sort(),
        dateRange,
        customDates,
        currency,
        selectedUserIds: selectedUsers.map(u => u.username).sort()
      });
      
      // Skip if we're already fetching with the same parameters
      if (lastFetchParams === currentFetchParams) {
        console.log('[Analytics] Skipping fetch - same parameters as last fetch');
        return;
      }
      
      // Update last fetch params
      setLastFetchParams(currentFetchParams);
      
      setLoading(true);
      setLoadingDirectReports(true);
      setLoadingDirectPlayers(true);
      setError(null);
      
      try {
        // Calculate date range in Lebanon time (same for all requests)
        const { startDate, endDate } = getDateRange(dateRange, customDates);
        
        // Get auth token
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();
        
        // === TRY PRE-CALCULATED AGGREGATIONS ===
        // For non-player selections, try aggregated data for direct reports first
        const selectedUser = selectedUsers[0];
        const isNonPlayer = selectedUser && selectedUser.type !== 'player';
        const directReports = getDirectReports();
        
        console.log(`[Reports Aggregations] selectedUser.type=${selectedUser?.type}, isNonPlayer=${isNonPlayer}, directReports=${directReports.length}, directReportUsernames=`, directReports.map(r => r.username).slice(0, 10));
        
        if (isNonPlayer && directReports.length > 0) {
          console.log(`[Reports Aggregations] Attempting aggregation before player-level fetch...`);
          const aggResult = await tryAggregationsWithFallback(
            directReports, startDate, endDate, currency, token
          );
          
          console.log(`[Reports Aggregations] Result: found=${aggResult.found}, records=${aggResult.data.length}`);
          
          if (aggResult.found) {
            console.log(`[Unified Fetch] Using ${aggResult.data.length} pre-calculated aggregation records — skipping player-level fetch`);
            const allAggData = aggResult.data;
            const { entityToReportMapping } = aggResult;
            
            if (requestId !== currentRequestIdRef.current) return;
            
            // 1. Main analytics chart data
            let isHourly = dateRange === 'last7' || dateRange === 'today';
            if (dateRange === 'custom' && customDates.startDate && customDates.endDate) {
              const daysDiff = Math.ceil((customDates.endDate - customDates.startDate) / (1000 * 60 * 60 * 24));
              isHourly = daysDiff <= 7;
            }
            const chartData = aggregateByDate(allAggData, isHourly, false);
            if (requestId === currentRequestIdRef.current) {
              setAnalyticsData(chartData);
            }
            
            // 2. Direct reports breakdown from aggregated data
            const reportTotalsMap = new Map();
            directReports.forEach(r => {
              reportTotalsMap.set(r.username, {
                total: { ggr: 0, ngr: 0, turnover: 0 },
                casino: { ggr: 0, ngr: 0, turnover: 0 },
                sport: { ggr: 0, ngr: 0, turnover: 0 },
                playerCount: 0
              });
            });
            
            allAggData.forEach(record => {
              const userId = record.user_id || record.userId;
              const reportUsername = entityToReportMapping.get(userId);
              if (!reportUsername) return;
              const totals = reportTotalsMap.get(reportUsername);
              if (!totals) return;
              
              const ggr = parseFloat(record.ggr || 0);
              const ngr = parseFloat(record.ngr || 0);
              const turnover = parseFloat(record.turnover || 0);
              
              totals.total.ggr += ggr;
              totals.total.ngr += ngr;
              totals.total.turnover += turnover;
              
              if (record.platform === 'pm8') {
                totals.casino.ggr += ggr;
                totals.casino.ngr += ngr;
                totals.casino.turnover += turnover;
              } else if (record.platform === 'sport') {
                totals.sport.ggr += ggr;
                totals.sport.ngr += ngr;
                totals.sport.turnover += turnover;
              }
              
              if (record.aggregated_player_count) {
                totals.playerCount = Math.max(totals.playerCount, record.aggregated_player_count);
              }
            });
            
            const reportsWithAnalytics = directReports.map(report => {
              const totals = reportTotalsMap.get(report.username);
              const hasData = totals && (totals.total.ggr !== 0 || totals.total.ngr !== 0 || totals.total.turnover !== 0);
              return {
                ...report,
                analytics: hasData ? { total: totals.total, casino: totals.casino, sport: totals.sport } : null,
                playerCount: totals?.playerCount || getAllPlayersUnderNode(report).length
              };
            });
            
            if (requestId === currentRequestIdRef.current) {
              setDirectReportsData(reportsWithAnalytics);
              setDirectPlayerAnalytics(null);
            }
            
            return; // Aggregated path done — finally block handles cleanup
          }
        }
        // === END AGGREGATION ATTEMPT — fall through to player-level fetch ===
        
        // Collect ALL unique player IDs needed
        const allPlayerIdsSet = new Set();
        
        // 1. Add effective players (main analytics)
        effectivePlayers.forEach(id => allPlayerIdsSet.add(id));
        
        // 2. Add players for direct reports
        const directReportsFallback = directReports.length > 0 ? directReports : getDirectReports();
        const reportPlayerMapping = new Map();
        directReportsFallback.forEach(report => {
          const playersUnder = report.type === 'player' 
            ? [report] 
            : getAllPlayersUnderNode(report);
          const playerIds = playersUnder.map(p => p.username);
          reportPlayerMapping.set(report.username, playerIds);
          playerIds.forEach(id => allPlayerIdsSet.add(id));
        });
        
        // 3. Add direct players (for super-agents)
        let directPlayersForSuperAgent = [];
        if (selectedUsers.length === 1 && selectedUsers[0]?.type === 'super-agent') {
          directPlayersForSuperAgent = getDirectPlayers();
          directPlayersForSuperAgent.forEach(player => {
            allPlayerIdsSet.add(player.username);
          });
        }
        
        // Convert to array
        const allUniquePlayerIds = Array.from(allPlayerIdsSet);
        
        console.log(`[Unified Fetch] Collecting data for ${allUniquePlayerIds.length} unique players across all views`);
        
        if (allUniquePlayerIds.length === 0) {
          setAnalyticsData([]);
          setDirectReportsData([]);
          setDirectPlayerAnalytics(null);
          return;
        }
        
        // Make ONE single API call with all player IDs
        const analyticsResult = await queueRequest(() => 
          fetchCombinedAnalytics(allUniquePlayerIds, startDate, endDate, currency, token)
        );
        
        const allAnalyticsData = analyticsResult.data;
        const isPreAggregatedByDay = analyticsResult.aggregatedByDay;
        
        console.log(`[Unified Fetch] Processing ${allAnalyticsData.length} records for distribution`);
        if (isPreAggregatedByDay) {
          console.log(`[Unified Fetch] Data is pre-aggregated by day from backend`);
        }
        
        // Debug: Log first few records to see date_hour format
        if (allAnalyticsData.length > 0) {
          console.log('[Debug] Sample date_hour values from backend:', 
            allAnalyticsData.slice(0, 3).map(r => r.date_hour)
          );
        }
        
        // Create a map for quick lookup
        const playerDataMap = new Map();
        allAnalyticsData.forEach(record => {
          const userId = record.user_id || record.userId;
          if (!playerDataMap.has(userId)) {
            playerDataMap.set(userId, []);
          }
          playerDataMap.get(userId).push(record);
        });
        
        // Check if this request is still valid before updating state
        if (requestId !== currentRequestIdRef.current) {
          console.log(`[Unified Fetch] Request #${requestId} is stale (current: ${currentRequestIdRef.current}), ignoring results`);
          return;
        }
        
        // 1. Process main analytics data
        if (effectivePlayers.length > 0) {
          const mainAnalyticsData = [];
          effectivePlayers.forEach(playerId => {
            const records = playerDataMap.get(playerId) || [];
            mainAnalyticsData.push(...records);
          });
          
          // Aggregate by date/hour
          let isHourly = dateRange === 'last7' || dateRange === 'today';
          if (dateRange === 'custom' && customDates.startDate && customDates.endDate) {
            const daysDiff = Math.ceil((customDates.endDate - customDates.startDate) / (1000 * 60 * 60 * 24));
            isHourly = daysDiff <= 7;
          }
          
          const aggregatedData = aggregateByDate(mainAnalyticsData, isHourly, isPreAggregatedByDay);
          
          // Double-check request is still valid before setting state
          if (requestId === currentRequestIdRef.current) {
            setAnalyticsData(aggregatedData);
          }
        } else {
          if (requestId === currentRequestIdRef.current) {
            setAnalyticsData([]);
          }
        }
        
        // 2. Process direct reports data
        if (directReportsFallback.length > 0) {
          const reportsWithAnalytics = directReportsFallback.map(report => {
            const playerIds = reportPlayerMapping.get(report.username) || [];
            
            if (playerIds.length === 0) {
              return {
                ...report,
                analytics: null,
                playerCount: 0
              };
            }
            
            // Aggregate analytics for this report's players
            const totals = {
              total: { ggr: 0, ngr: 0, turnover: 0 },
              casino: { ggr: 0, ngr: 0, turnover: 0 },
              sport: { ggr: 0, ngr: 0, turnover: 0 }
            };
            
            playerIds.forEach(playerId => {
              const playerData = playerDataMap.get(playerId) || [];
              playerData.forEach(record => {
                const ggr = parseFloat(record.ggr || 0);
                const ngr = parseFloat(record.ngr || 0);
                const turnover = parseFloat(record.turnover || 0);
                
                totals.total.ggr += ggr;
                totals.total.ngr += ngr;
                totals.total.turnover += turnover;
                
                if (record.platform === 'pm8') {
                  totals.casino.ggr += ggr;
                  totals.casino.ngr += ngr;
                  totals.casino.turnover += turnover;
                } else if (record.platform === 'sport') {
                  totals.sport.ggr += ggr;
                  totals.sport.ngr += ngr;
                  totals.sport.turnover += turnover;
                }
              });
            });
            
            return {
              ...report,
              analytics: totals,
              playerCount: playerIds.length
            };
          });
          
          // Check request validity before setting state
          if (requestId === currentRequestIdRef.current) {
            setDirectReportsData(reportsWithAnalytics);
          }
        } else {
          if (requestId === currentRequestIdRef.current) {
            setDirectReportsData([]);
          }
        }
        
        // 3. Process direct player analytics for super-agents
        if (directPlayersForSuperAgent.length > 0) {
          const totals = {
            ggr: 0,
            ngr: 0,
            turnover: 0,
            casinoGGR: 0,
            casinoNGR: 0,
            casinoTurnover: 0,
            sportGGR: 0,
            sportNGR: 0,
            sportTurnover: 0
          };
          
          directPlayersForSuperAgent.forEach(player => {
            const playerData = playerDataMap.get(player.username) || [];
            playerData.forEach(record => {
              const ggr = parseFloat(record.ggr || 0);
              const ngr = parseFloat(record.ngr || 0);
              const turnover = parseFloat(record.turnover || 0);
              
              totals.ggr += ggr;
              totals.ngr += ngr;
              totals.turnover += turnover;
              
              if (record.platform === 'pm8') {
                totals.casinoGGR += ggr;
                totals.casinoNGR += ngr;
                totals.casinoTurnover += turnover;
              } else if (record.platform === 'sport') {
                totals.sportGGR += ggr;
                totals.sportNGR += ngr;
                totals.sportTurnover += turnover;
              }
            });
          });
          
          // Check request validity before setting state
          if (requestId === currentRequestIdRef.current) {
            setDirectPlayerAnalytics({
              totals,
              playerCount: directPlayersForSuperAgent.length,
              players: directPlayersForSuperAgent
            });
          }
        } else {
          if (requestId === currentRequestIdRef.current) {
            setDirectPlayerAnalytics(null);
          }
        }
        
      } catch (error) {
        console.error('[Unified Fetch] Error:', error);
        setError(error.message || 'Failed to fetch analytics data');
        setAnalyticsData([]);
        setDirectReportsData([]);
        setDirectPlayerAnalytics(null);
        // Reset last fetch params on error so retry is possible
        setLastFetchParams(null);
      } finally {
        setLoading(false);
        setLoadingDirectReports(false);
        setLoadingDirectPlayers(false);
      }
    };
    
    // Add a small debounce to handle rapid state changes during initialization
    const timeoutId = setTimeout(() => {
      fetchAllAnalyticsInOneCall();
    }, 100);
    
    // Cleanup function to clear timeout if dependencies change before it fires
    return () => clearTimeout(timeoutId);
  }, [dateRange, customDates, effectivePlayers, currency, selectedUsers, isInitialized]);

  // Direct player analytics is now handled in the consolidated useEffect above

  // Handle refresh button click - uses the same optimized single-call approach
  const handleRefresh = async () => {
    setIsRefreshing(true);
    
    // Reset last fetch params to force a fresh fetch
    setLastFetchParams(null);
    
    // Increment request ID to invalidate any previous in-flight requests
    currentRequestIdRef.current = currentRequestIdRef.current + 1;
    const requestId = currentRequestIdRef.current;
    console.log(`[Refresh] Starting new request #${requestId}`);
    
    // Force a re-run of the useEffect by updating a dummy state
    // This ensures we use the same optimized single-call logic
    const fetchAllAnalyticsInOneCall = async () => {
      // Skip if no players to fetch
      if (effectivePlayers.length === 0 && selectedUsers.length === 0) {
        setAnalyticsData([]);
        setDirectReportsData([]);
        setDirectPlayerAnalytics(null);
        return;
      }
      
      setLoading(true);
      setLoadingDirectReports(true);
      setLoadingDirectPlayers(true);
      setError(null);
      
      try {
        // Calculate date range in Lebanon time (same for all requests)
        const { startDate, endDate } = getDateRange(dateRange, customDates);
        
        // Get auth token
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();
        
        // === TRY PRE-CALCULATED AGGREGATIONS ===
        const selectedUser = selectedUsers[0];
        const isNonPlayer = selectedUser && selectedUser.type !== 'player';
        const directReports = getDirectReports();
        
        console.log(`[Refresh Aggregations] selectedUser.type=${selectedUser?.type}, isNonPlayer=${isNonPlayer}, directReports=${directReports.length}`);
        
        if (isNonPlayer && directReports.length > 0) {
          console.log(`[Refresh Aggregations] Attempting aggregation before player-level fetch...`);
          const aggResult = await tryAggregationsWithFallback(
            directReports, startDate, endDate, currency, token
          );
          
          console.log(`[Refresh Aggregations] Result: found=${aggResult.found}, records=${aggResult.data.length}`);
          
          if (aggResult.found) {
            console.log(`[Refresh] Using ${aggResult.data.length} pre-calculated aggregation records — skipping player-level fetch`);
            const allAggData = aggResult.data;
            const { entityToReportMapping } = aggResult;
            
            if (requestId !== currentRequestIdRef.current) return;
            
            let isHourly = dateRange === 'last7' || dateRange === 'today';
            if (dateRange === 'custom' && customDates.startDate && customDates.endDate) {
              const daysDiff = Math.ceil((customDates.endDate - customDates.startDate) / (1000 * 60 * 60 * 24));
              isHourly = daysDiff <= 7;
            }
            const chartData = aggregateByDate(allAggData, isHourly, false);
            if (requestId === currentRequestIdRef.current) {
              setAnalyticsData(chartData);
            }
            
            const reportTotalsMap = new Map();
            directReports.forEach(r => {
              reportTotalsMap.set(r.username, {
                total: { ggr: 0, ngr: 0, turnover: 0 },
                casino: { ggr: 0, ngr: 0, turnover: 0 },
                sport: { ggr: 0, ngr: 0, turnover: 0 },
                playerCount: 0
              });
            });
            
            allAggData.forEach(record => {
              const userId = record.user_id || record.userId;
              const reportUsername = entityToReportMapping.get(userId);
              if (!reportUsername) return;
              const totals = reportTotalsMap.get(reportUsername);
              if (!totals) return;
              
              const ggr = parseFloat(record.ggr || 0);
              const ngr = parseFloat(record.ngr || 0);
              const turnover = parseFloat(record.turnover || 0);
              
              totals.total.ggr += ggr;
              totals.total.ngr += ngr;
              totals.total.turnover += turnover;
              
              if (record.platform === 'pm8') {
                totals.casino.ggr += ggr;
                totals.casino.ngr += ngr;
                totals.casino.turnover += turnover;
              } else if (record.platform === 'sport') {
                totals.sport.ggr += ggr;
                totals.sport.ngr += ngr;
                totals.sport.turnover += turnover;
              }
              
              if (record.aggregated_player_count) {
                totals.playerCount = Math.max(totals.playerCount, record.aggregated_player_count);
              }
            });
            
            const reportsWithAnalytics = directReports.map(report => {
              const totals = reportTotalsMap.get(report.username);
              const hasData = totals && (totals.total.ggr !== 0 || totals.total.ngr !== 0 || totals.total.turnover !== 0);
              return {
                ...report,
                analytics: hasData ? { total: totals.total, casino: totals.casino, sport: totals.sport } : null,
                playerCount: totals?.playerCount || getAllPlayersUnderNode(report).length
              };
            });
            
            if (requestId === currentRequestIdRef.current) {
              setDirectReportsData(reportsWithAnalytics);
              setDirectPlayerAnalytics(null);
            }
            
            return;
          }
        }
        // === END AGGREGATION ATTEMPT ===
        
        // Collect ALL unique player IDs needed
        const allPlayerIdsSet = new Set();
        
        // 1. Add effective players (main analytics)
        effectivePlayers.forEach(id => allPlayerIdsSet.add(id));
        
        // 2. Add players for direct reports
        const directReportsFallback = directReports.length > 0 ? directReports : getDirectReports();
        const reportPlayerMapping = new Map();
        directReportsFallback.forEach(report => {
          const playersUnder = report.type === 'player' 
            ? [report] 
            : getAllPlayersUnderNode(report);
          const playerIds = playersUnder.map(p => p.username);
          reportPlayerMapping.set(report.username, playerIds);
          playerIds.forEach(id => allPlayerIdsSet.add(id));
        });
        
        // 3. Add direct players (for super-agents)
        let directPlayersForSuperAgent = [];
        if (selectedUsers.length === 1 && selectedUsers[0]?.type === 'super-agent') {
          directPlayersForSuperAgent = getDirectPlayers();
          directPlayersForSuperAgent.forEach(player => {
            allPlayerIdsSet.add(player.username);
          });
        }
        
        // Convert to array
        const allUniquePlayerIds = Array.from(allPlayerIdsSet);
        
        console.log(`[Refresh] Collecting data for ${allUniquePlayerIds.length} unique players across all views`);
        
        if (allUniquePlayerIds.length === 0) {
          setAnalyticsData([]);
          setDirectReportsData([]);
          setDirectPlayerAnalytics(null);
          return;
        }
        
        // Make ONE single API call with all player IDs
        const analyticsResult = await queueRequest(() => 
          fetchCombinedAnalytics(allUniquePlayerIds, startDate, endDate, currency, token)
        );
        
        const allAnalyticsData = analyticsResult.data;
        const isPreAggregatedByDay = analyticsResult.aggregatedByDay;
        
        console.log(`[Refresh] Processing ${allAnalyticsData.length} records for distribution`);
        if (isPreAggregatedByDay) {
          console.log(`[Refresh] Data is pre-aggregated by day from backend`);
        }
        
        // Create a map for quick lookup
        const playerDataMap = new Map();
        allAnalyticsData.forEach(record => {
          const userId = record.user_id || record.userId;
          if (!playerDataMap.has(userId)) {
            playerDataMap.set(userId, []);
          }
          playerDataMap.get(userId).push(record);
        });
        
        // Check if this request is still valid before updating state
        if (requestId !== currentRequestIdRef.current) {
          console.log(`[Refresh] Request #${requestId} is stale (current: ${currentRequestIdRef.current}), ignoring results`);
          return;
        }
        
        // 1. Process main analytics data
        if (effectivePlayers.length > 0) {
          const mainAnalyticsData = [];
          effectivePlayers.forEach(playerId => {
            const records = playerDataMap.get(playerId) || [];
            mainAnalyticsData.push(...records);
          });
          
          // Aggregate by date/hour
          let isHourly = dateRange === 'last7' || dateRange === 'today';
          if (dateRange === 'custom' && customDates.startDate && customDates.endDate) {
            const daysDiff = Math.ceil((customDates.endDate - customDates.startDate) / (1000 * 60 * 60 * 24));
            isHourly = daysDiff <= 7;
          }
          
          const aggregatedData = aggregateByDate(mainAnalyticsData, isHourly, isPreAggregatedByDay);
          
          // Double-check request is still valid before setting state
          if (requestId === currentRequestIdRef.current) {
            setAnalyticsData(aggregatedData);
          }
        } else {
          if (requestId === currentRequestIdRef.current) {
            setAnalyticsData([]);
          }
        }
        
        // 2. Process direct reports data
        if (directReportsFallback.length > 0) {
          const reportsWithAnalytics = directReportsFallback.map(report => {
            const playerIds = reportPlayerMapping.get(report.username) || [];
            
            if (playerIds.length === 0) {
              return {
                ...report,
                analytics: null,
                playerCount: 0
              };
            }
            
            // Aggregate analytics for this report's players
            const totals = {
              total: { ggr: 0, ngr: 0, turnover: 0 },
              casino: { ggr: 0, ngr: 0, turnover: 0 },
              sport: { ggr: 0, ngr: 0, turnover: 0 }
            };
            
            playerIds.forEach(playerId => {
              const playerData = playerDataMap.get(playerId) || [];
              playerData.forEach(record => {
                const ggr = parseFloat(record.ggr || 0);
                const ngr = parseFloat(record.ngr || 0);
                const turnover = parseFloat(record.turnover || 0);
                
                totals.total.ggr += ggr;
                totals.total.ngr += ngr;
                totals.total.turnover += turnover;
                
                if (record.platform === 'pm8') {
                  totals.casino.ggr += ggr;
                  totals.casino.ngr += ngr;
                  totals.casino.turnover += turnover;
                } else if (record.platform === 'sport') {
                  totals.sport.ggr += ggr;
                  totals.sport.ngr += ngr;
                  totals.sport.turnover += turnover;
                }
              });
            });
            
            return {
              ...report,
              analytics: totals,
              playerCount: playerIds.length
            };
          });
          
          // Check request validity before setting state
          if (requestId === currentRequestIdRef.current) {
            setDirectReportsData(reportsWithAnalytics);
          }
        } else {
          if (requestId === currentRequestIdRef.current) {
            setDirectReportsData([]);
          }
        }
        
        // 3. Process direct player analytics for super-agents
        if (directPlayersForSuperAgent.length > 0) {
          const totals = {
            ggr: 0,
            ngr: 0,
            turnover: 0,
            casinoGGR: 0,
            casinoNGR: 0,
            casinoTurnover: 0,
            sportGGR: 0,
            sportNGR: 0,
            sportTurnover: 0
          };
          
          directPlayersForSuperAgent.forEach(player => {
            const playerData = playerDataMap.get(player.username) || [];
            playerData.forEach(record => {
              const ggr = parseFloat(record.ggr || 0);
              const ngr = parseFloat(record.ngr || 0);
              const turnover = parseFloat(record.turnover || 0);
              
              totals.ggr += ggr;
              totals.ngr += ngr;
              totals.turnover += turnover;
              
              if (record.platform === 'pm8') {
                totals.casinoGGR += ggr;
                totals.casinoNGR += ngr;
                totals.casinoTurnover += turnover;
              } else if (record.platform === 'sport') {
                totals.sportGGR += ggr;
                totals.sportNGR += ngr;
                totals.sportTurnover += turnover;
              }
            });
          });
          
          // Check request validity before setting state
          if (requestId === currentRequestIdRef.current) {
            setDirectPlayerAnalytics({
              totals,
              playerCount: directPlayersForSuperAgent.length,
              players: directPlayersForSuperAgent
            });
          }
        } else {
          if (requestId === currentRequestIdRef.current) {
            setDirectPlayerAnalytics(null);
          }
        }
        
      } catch (error) {
        console.error('[Refresh] Error:', error);
        setError(error.message || 'Failed to fetch analytics data');
        setAnalyticsData([]);
        setDirectReportsData([]);
        setDirectPlayerAnalytics(null);
      } finally {
        setLoading(false);
        setLoadingDirectReports(false);
        setLoadingDirectPlayers(false);
        setIsRefreshing(false);
      }
    };
    
    await fetchAllAnalyticsInOneCall();
  };

  // Helper function to get severity color based on discrepancy percentage
  const getSeverityColor = (mismatchPercent) => {
    if (mismatchPercent === 0) {
      return {
        bg: 'bg-green-50 dark:bg-green-900/10',
        border: 'border-green-300 dark:border-green-700',
        text: 'text-green-700 dark:text-green-300',
        badge: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200',
        icon: 'text-green-600 dark:text-green-400'
      };
    } else if (mismatchPercent < 5) {
      return {
        bg: 'bg-yellow-50 dark:bg-yellow-900/10',
        border: 'border-yellow-300 dark:border-yellow-700',
        text: 'text-yellow-700 dark:text-yellow-300',
        badge: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200',
        icon: 'text-yellow-600 dark:text-yellow-400'
      };
    } else if (mismatchPercent < 10) {
      return {
        bg: 'bg-orange-50 dark:bg-orange-900/10',
        border: 'border-orange-300 dark:border-orange-700',
        text: 'text-orange-700 dark:text-orange-300',
        badge: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200',
        icon: 'text-orange-600 dark:text-orange-400'
      };
    } else if (mismatchPercent < 100) {
      return {
        bg: 'bg-red-50 dark:bg-red-900/10',
        border: 'border-red-300 dark:border-red-700',
        text: 'text-red-700 dark:text-red-300',
        badge: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200',
        icon: 'text-red-600 dark:text-red-400'
      };
    } else {
      // >100% - critical
      return {
        bg: 'bg-purple-50 dark:bg-purple-900/10',
        border: 'border-purple-300 dark:border-purple-700',
        text: 'text-purple-700 dark:text-purple-300',
        badge: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200',
        icon: 'text-purple-600 dark:text-purple-400'
      };
    }
  };

  // Toggle player expanded state
  const togglePlayerExpanded = (playerUsername) => {
    setExpandedPlayers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(playerUsername)) {
        newSet.delete(playerUsername);
      } else {
        newSet.add(playerUsername);
      }
      return newSet;
    });
  };

  // Toggle round expanded state in deep dive
  const toggleRoundExpanded = (roundId) => {
    setExpandedRounds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(roundId)) {
        newSet.delete(roundId);
      } else {
        newSet.add(roundId);
      }
      return newSet;
    });
  };

  // Deep Dive Analysis
  const handleDeepDive = async (playerUsername) => {
    if (!isAdmin) {
      console.error('Deep Dive: User is not admin');
      return;
    }

    setDeepDiveLoading(true);
    setDeepDiveResults(null);
    setExpandedRounds(new Set());
    
    try {
      console.log('[Deep Dive] Starting analysis for player:', playerUsername);
      
      // Find the player in analysis results
      const playerData = ggrAnalysisResults?.playerAnalysis.find(p => p.player === playerUsername);
      if (!playerData) {
        setError('Player data not found');
        setDeepDiveLoading(false);
        return;
      }
      
      setDeepDivePlayer(playerData);
      
      // Get date range
      const { startDate, endDate } = getDateRange(dateRange, customDates);
      
      // Get auth token
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      console.log('[Deep Dive] Fetching game transactions...');
      
      // Fetch all game transactions for this player
      const response = await apiPost(
        'analytics',
        '/gametransactions',
        {
          playerIds: [playerUsername],
          startDate: format(startDate, 'yyyy-MM-dd'),
          endDate: format(endDate, 'yyyy-MM-dd'),
          platform: undefined, // Get both casino and sport
          limit: 10000
        },
        token
      );
      
      if (!response.success || !response.data) {
        throw new Error('Failed to fetch game transactions');
      }
      
      let gameTransactions = response.data;
      console.log('[Deep Dive] Fetched', gameTransactions.length, 'game transactions from API');
      
      // CRITICAL: Filter to only transactions within the selected date range
      // The API might return transactions outside the range, so we must filter them
      const startTimestamp = startDate.getTime();
      const endTimestamp = endDate.getTime();
      
      gameTransactions = gameTransactions.filter(tx => {
        const txTimestamp = tx.timestamp || tx.createdAt || 0;
        return txTimestamp >= startTimestamp && txTimestamp <= endTimestamp;
      });
      
      console.log('[Deep Dive] After filtering to date range:', gameTransactions.length, 'transactions remain');
      console.log('[Deep Dive] Date range:', format(startDate, 'yyyy-MM-dd HH:mm'), 'to', format(endDate, 'yyyy-MM-dd HH:mm'));
      console.log('[Deep Dive] Sample transactions:', gameTransactions.slice(0, 3));
      
      // Group transactions by round/bet
      const groupedRounds = groupGameTransactionsByRound(gameTransactions);
      console.log('[Deep Dive] Grouped into', groupedRounds.length, 'rounds/bets');
      
      // Calculate GGR for each round and identify issues
      const analyzedRounds = groupedRounds.map(roundTxs => {
        const roundData = calculateRoundGGR(roundTxs);
        const roundIssues = identifyRoundIssues(roundData, startDate, endDate);
        
        return {
          ...roundData,
          ...roundIssues
        };
      });
      
      // Separate by platform
      const casinoRounds = analyzedRounds.filter(r => r.isCasino);
      const sportRounds = analyzedRounds.filter(r => r.isSportsBet);
      
      // Calculate totals
      const calculatedCasinoGGR = casinoRounds.reduce((sum, r) => sum + r.roundGGR, 0);
      const calculatedSportGGR = sportRounds.reduce((sum, r) => sum + r.roundGGR, 0);
      const totalCalculatedGGR = calculatedCasinoGGR + calculatedSportGGR;
      
      // Get analytics breakdown by platform
      const analyticsDataArray = ggrAnalysisResults?.playerAnalysis
        .find(p => p.player === playerUsername)?.analyticsData || [];
      
      const analyticsCasinoGGR = analyticsDataArray
        .filter(r => r.platform === 'pm8')
        .reduce((sum, r) => sum + (parseFloat(r.ggr) || 0), 0);
        
      const analyticsSportGGR = analyticsDataArray
        .filter(r => r.platform === 'sport')
        .reduce((sum, r) => sum + (parseFloat(r.ggr) || 0), 0);
      
      // Compare
      const comparison = {
        analyticsGGR: playerData.analyticsGGR,
        calculatedGGR: totalCalculatedGGR,
        discrepancy: playerData.analyticsGGR - totalCalculatedGGR,
        discrepancyPercent: playerData.analyticsGGR !== 0 
          ? ((playerData.analyticsGGR - totalCalculatedGGR) / Math.abs(playerData.analyticsGGR)) * 100
          : 0,
        
        byPlatform: {
          casino: {
            analytics: analyticsCasinoGGR,
            calculated: calculatedCasinoGGR,
            diff: analyticsCasinoGGR - calculatedCasinoGGR,
            diffPercent: analyticsCasinoGGR !== 0 
              ? ((analyticsCasinoGGR - calculatedCasinoGGR) / Math.abs(analyticsCasinoGGR)) * 100
              : 0
          },
          sport: {
            analytics: analyticsSportGGR,
            calculated: calculatedSportGGR,
            diff: analyticsSportGGR - calculatedSportGGR,
            diffPercent: analyticsSportGGR !== 0 
              ? ((analyticsSportGGR - calculatedSportGGR) / Math.abs(analyticsSportGGR)) * 100
              : 0
          }
        }
      };
      
      // Identify problematic rounds (with issues or large GGR impact)
      const problematicRounds = analyzedRounds
        .filter(r => 
          r.issues.length > 0 || 
          r.warnings.length > 0 || 
          Math.abs(r.roundGGR) > 50 // Flag significant GGR contributions
        )
        .sort((a, b) => {
          // Sort by: issues first, then by absolute GGR impact
          const aHasIssues = a.issues.length > 0 ? 1 : 0;
          const bHasIssues = b.issues.length > 0 ? 1 : 0;
          if (aHasIssues !== bHasIssues) return bHasIssues - aHasIssues;
          return Math.abs(b.roundGGR) - Math.abs(a.roundGGR);
        });
      
      const results = {
        player: playerData,
        comparison,
        totalRounds: analyzedRounds.length,
        casinoRounds: casinoRounds.length,
        sportRounds: sportRounds.length,
        problematicRounds,
        allRounds: analyzedRounds,
        dateRange: { startDate, endDate },
        currency
      };
      
      console.log('[Deep Dive] Analysis complete:', results);
      setDeepDiveResults(results);
      setShowDeepDiveModal(true);
      
    } catch (error) {
      console.error('[Deep Dive] Error:', error);
      setError(`Deep Dive Analysis failed: ${error.message}`);
    } finally {
      setDeepDiveLoading(false);
    }
  };

  // GGR Analysis Functions
  const handleGGRAnalysis = async () => {
    if (!isAdmin) {
      console.error('GGR Analysis: User is not admin');
      return;
    }

    if (selectedUsers.length === 0) {
      setError('Please select a user to analyze');
      return;
    }

    setGGRAnalysisLoading(true);
    setGGRAnalysisResults(null);
    setExpandedPlayers(new Set()); // Reset expanded state

    try {
      console.log('[GGR Analysis] Starting analysis for:', selectedUsers);
      
      // Get date range
      const { startDate, endDate } = getDateRange(dateRange, customDates);
      
      // Get all players under the selected user(s)
      let allPlayers = [];
      selectedUsers.forEach(user => {
        if (user.type === 'player') {
          allPlayers.push(user);
        } else {
          // Get all players under this agent
          const playersUnder = getAllPlayersUnderNode(user);
          allPlayers.push(...playersUnder);
        }
      });

      // Remove duplicates
      allPlayers = [...new Map(allPlayers.map(p => [p.username, p])).values()];
      
      if (allPlayers.length === 0) {
        setError('No players found under the selected user');
        setGGRAnalysisLoading(false);
        return;
      }

      console.log('[GGR Analysis] Analyzing', allPlayers.length, 'players');

      // Get auth token
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      // 1. Fetch Analytics Data (GGR from analytics table)
      const playerIds = allPlayers.map(p => p.username);
      const analyticsResult = await fetchCombinedAnalytics(
        playerIds,
        startDate,
        endDate,
        currency,
        token
      );

      console.log('[GGR Analysis] Analytics data fetched:', analyticsResult);
      console.log('[GGR Analysis] Analytics data sample:', analyticsResult.data?.slice(0, 3));
      console.log('[GGR Analysis] Analytics data count:', analyticsResult.data?.length);

      // 2. Fetch Wallet Transactions
      // Query wallet transactions via the querytransact API
      let walletTransactions = [];
      
      // Fetch transactions for each player (could be optimized with batch API if available)
      try {
        const queryStartDate = format(startDate, 'yyyy-MM-dd');
        const queryEndDate = format(endDate, 'yyyy-MM-dd');
        
        // Fetch transactions where players are targets (deposits/withdrawals)
        for (const playerId of playerIds.slice(0, 10)) { // Limit to first 10 players to avoid timeout
          try {
            const queryParams = new URLSearchParams();
            queryParams.append('targetAgent', playerId);
            queryParams.append('startDate', queryStartDate);
            queryParams.append('endDate', queryEndDate);
            
            const path = `/querytransact?${queryParams.toString()}`;
            
            const result = await get({
              apiName: 'querytransact',
              path: path
            }).response;
            
            const data = await result.body.json();
            console.log(`[GGR Analysis] Transaction response for ${playerId}:`, data);
            
            // Handle different response formats
            if (data.transactions && Array.isArray(data.transactions)) {
              walletTransactions.push(...data.transactions);
            } else if (Array.isArray(data)) {
              // Response might be an array directly
              walletTransactions.push(...data);
            } else if (data.data && Array.isArray(data.data)) {
              // Response might have a data property
              walletTransactions.push(...data.data);
            }
          } catch (playerTxError) {
            console.warn(`[GGR Analysis] Could not fetch transactions for player ${playerId}:`, playerTxError);
          }
        }
        
        console.log('[GGR Analysis] Wallet transactions fetched:', walletTransactions.length);
        console.log('[GGR Analysis] Sample transactions:', walletTransactions.slice(0, 3));
      } catch (txError) {
        console.warn('[GGR Analysis] Error fetching wallet transactions:', txError);
        // Continue without wallet transactions
      }

      // Make sure analytics data exists
      const analyticsDataArray = analyticsResult.data || [];
      if (analyticsDataArray.length === 0) {
        console.warn('[GGR Analysis] No analytics data returned for players');
      }

      // 3. Analyze each player
      const playerAnalysisPromises = allPlayers.map(async (player) => {
        // Get analytics data for this player
        const playerAnalyticsData = analyticsDataArray.filter(record => {
          const recordUserId = record.user_id || record.userId;
          return recordUserId === player.username;
        });

        // Try different currency key variations
        const currencyLower = currency.toLowerCase();
        const currencyUpper = currency.toUpperCase();
        const currentBalance = 
          player.balances?.[currencyLower] ?? 
          player.balances?.[currencyUpper] ?? 
          player.balances?.[currency] ?? 
          0;

        console.log(`[GGR Analysis] Player ${player.username}:`, {
          analyticsRecords: playerAnalyticsData.length,
          currentBalance,
          balances: player.balances,
          currency,
          currencyKey: currencyLower
        });

        // Calculate GGR from analytics
        const analyticsGGR = playerAnalyticsData.reduce((sum, record) => {
          return sum + (parseFloat(record.ggr) || 0);
        }, 0);

        const analyticsNGR = playerAnalyticsData.reduce((sum, record) => {
          return sum + (parseFloat(record.ngr) || 0);
        }, 0);

        const analyticsTurnover = playerAnalyticsData.reduce((sum, record) => {
          return sum + (parseFloat(record.turnover) || 0);
        }, 0);

        // Get wallet transactions for this player
        const playerTransactions = walletTransactions.filter(
          tx => tx.targetAgent === player.username || tx.initiator === player.username
        );

        console.log(`[GGR Analysis] Player ${player.username}: ${playerTransactions.length} transactions`);

        // Calculate deposits and withdrawals
        let totalDeposits = 0;
        let totalWithdrawals = 0;
        let startingBalance = null; // Balance at the START of the selected period
        let endingBalance = null; // Balance at the END of the selected period

        // Sort transactions by timestamp (earliest to latest)
        const sortedTx = [...playerTransactions].sort((a, b) => a.timestamp - b.timestamp);
        
        // Filter to only transactions TO this player (targetAgent)
        const playerTargetTx = sortedTx.filter(tx => tx.targetAgent === player.username);

        console.log(`[GGR Analysis] ${player.username} - Processing ${playerTargetTx.length} transactions to this player (out of ${sortedTx.length} total)`);

        playerTargetTx.forEach((tx, idx) => {
          const amount = parseFloat(tx.amount) || 0;
          const targetBalance = parseFloat(tx.targetBalance) || 0;
          
          if (tx.isWithdrawal) {
            totalWithdrawals += amount;
          } else {
            totalDeposits += amount;
          }

          // First transaction: targetBalance is the balance BEFORE this transaction
          if (startingBalance === null) {
            startingBalance = targetBalance;
            console.log(`[GGR Analysis] ${player.username} - Starting balance: ${startingBalance} (from first tx)`);
          }
          
          // Last transaction in sorted array = most recent transaction
          // Calculate balance AFTER this transaction
          const balanceAfter = tx.isWithdrawal
            ? targetBalance - amount
            : targetBalance + amount;
          
          // Keep updating endingBalance - the last one will be from the most recent transaction
          endingBalance = balanceAfter;
          
          if (idx < 3 || idx === playerTargetTx.length - 1) {
            console.log(`[GGR Analysis] ${player.username} - TX ${idx}: ${tx.isWithdrawal ? 'Withdrawal' : 'Deposit'} ${amount}, Before: ${targetBalance}, After: ${balanceAfter}, Timestamp: ${tx.timestamp ? new Date(tx.timestamp).toISOString() : 'N/A'}`);
          }
        });
        
        // Now fetch game transactions to find the ACTUAL last transaction
        // The ending balance should be from whichever transaction happened last (wallet or game)
        let actualEndingBalance = endingBalance; // Start with wallet transaction ending
        let lastWalletTxTimestamp = playerTargetTx.length > 0 ? playerTargetTx[playerTargetTx.length - 1].timestamp : 0;
        
        try {
          const gameResponse = await apiPost(
            'analytics',
            '/gametransactions',
            {
              playerIds: [player.username],
              startDate: format(startDate, 'yyyy-MM-dd'),
              endDate: format(endDate, 'yyyy-MM-dd'),
              platform: undefined, // Get both casino and sport
              limit: 1000
            },
            token
          );
          
          const gameTransactions = gameResponse.data || [];
          console.log(`[GGR Analysis] ${player.username} - Found ${gameTransactions.length} game transactions`);
          
          if (gameTransactions.length > 0) {
            // Filter game transactions to ONLY those within the selected date range
            const endDateTimestamp = endDate.getTime();
            const startDateTimestamp = startDate.getTime();
            
            const gameTransactionsInPeriod = gameTransactions.filter(tx => {
              const txTimestamp = tx.timestamp || tx.createdAt || 0;
              return txTimestamp >= startDateTimestamp && txTimestamp <= endDateTimestamp;
            });
            
            console.log(`[GGR Analysis] ${player.username} - Found ${gameTransactionsInPeriod.length} game transactions within period (out of ${gameTransactions.length} total)`);
            
            if (gameTransactionsInPeriod.length > 0) {
              // Sort by timestamp to find the last one
              const sortedGameTx = gameTransactionsInPeriod.sort((a, b) => {
                const tsA = a.timestamp || a.createdAt || 0;
                const tsB = b.timestamp || b.createdAt || 0;
                return tsA - tsB;
              });
              
              const lastGameTx = sortedGameTx[sortedGameTx.length - 1];
              const lastGameTxTimestamp = lastGameTx.timestamp || lastGameTx.createdAt || 0;
              
              console.log(`[GGR Analysis] ${player.username} - Period: ${format(startDate, 'yyyy-MM-dd HH:mm')} to ${format(endDate, 'yyyy-MM-dd HH:mm')}`);
              console.log(`[GGR Analysis] ${player.username} - Last wallet tx: ${lastWalletTxTimestamp ? new Date(lastWalletTxTimestamp).toISOString() : 'N/A'}, Last game tx: ${lastGameTxTimestamp ? new Date(lastGameTxTimestamp).toISOString() : 'N/A'}`);
              
              // If game transaction is more recent, use its balance
              if (lastGameTxTimestamp > lastWalletTxTimestamp) {
                const balanceAfter = lastGameTx.balanceAfter || {};
                const totalGameBalance = (parseFloat(balanceAfter.cash || 0) + 
                                         parseFloat(balanceAfter.bonus || 0) + 
                                         parseFloat(balanceAfter.locked || 0));
                actualEndingBalance = totalGameBalance;
                console.log(`[GGR Analysis] ${player.username} - Using game transaction balance: ${actualEndingBalance} (game tx is more recent, balanceAfter: ${JSON.stringify(balanceAfter)})`);
              } else {
                console.log(`[GGR Analysis] ${player.username} - Using wallet transaction balance: ${actualEndingBalance} (wallet tx is more recent)`);
              }
            } else {
              console.log(`[GGR Analysis] ${player.username} - No game transactions in period, using wallet transaction balance: ${actualEndingBalance}`);
            }
          }
        } catch (gameError) {
          console.error(`[GGR Analysis] ${player.username} - Error fetching game transactions:`, gameError);
          // Continue with wallet transaction balance
        }
        
        console.log(`[GGR Analysis] ${player.username} - Final: Starting=${startingBalance}, Ending=${actualEndingBalance}, Deposits=${totalDeposits}, Withdrawals=${totalWithdrawals}`);

        // Calculate expected vs actual balance changes
        const netFlow = totalDeposits - totalWithdrawals;
        
        // EXPECTED: Starting Balance + Deposits - Withdrawals - GGR
        const expectedBalanceChange = netFlow - analyticsGGR;
        const expectedEndingBalance = startingBalance !== null 
          ? startingBalance + expectedBalanceChange
          : null;
        
        // ACTUAL: actualEndingBalance was already calculated above by comparing wallet and game transactions
        // If no transactions at all, fallback to starting balance
        if (actualEndingBalance === null) {
          actualEndingBalance = startingBalance;
        }
        
        const actualBalanceChange = startingBalance !== null && actualEndingBalance !== null
          ? actualEndingBalance - startingBalance
          : null;

        // Check for mismatch
        let hasMismatch = false;
        let mismatchAmount = 0;
        let mismatchReason = '';

        // Compare expected vs actual ENDING balances (not changes)
        const diff = expectedEndingBalance !== null && actualEndingBalance !== null
          ? Math.abs(expectedEndingBalance - actualEndingBalance)
          : 0;
        
        console.log(`[GGR Analysis] ${player.username} Balance Check:`, {
          period: `${format(startDate, 'yyyy-MM-dd')} to ${format(endDate, 'yyyy-MM-dd')}`,
          startingBalance,
          deposits: totalDeposits,
          withdrawals: totalWithdrawals,
          analyticsGGR,
          expectedEndingBalance,
          actualEndingBalance,
          difference: diff,
          transactionCount: playerTransactions.length,
          calculation: `${startingBalance} + ${totalDeposits} - ${totalWithdrawals} - ${analyticsGGR} = ${expectedEndingBalance}`
        });

        // Consider anything > 0.01 as a mismatch (to account for rounding)
        if (diff > 0.01) {
          hasMismatch = true;
          mismatchAmount = expectedEndingBalance - actualEndingBalance;
          
          console.log(`[GGR Analysis] MISMATCH detected for ${player.username}:`, {
            diff,
            mismatchAmount,
            percentOfGGR: analyticsGGR !== 0 ? (Math.abs(mismatchAmount) / Math.abs(analyticsGGR) * 100).toFixed(1) + '%' : 'N/A'
          });
          
          if (Math.abs(mismatchAmount) > Math.abs(analyticsGGR) * 0.1) {
            mismatchReason = 'Significant discrepancy (>10% of GGR)';
          } else {
            mismatchReason = 'Minor discrepancy detected';
          }
        } else if (analyticsGGR !== 0 && playerTransactions.length === 0) {
          hasMismatch = true;
          mismatchReason = 'GGR exists but no wallet transactions found';
        } else if (analyticsGGR === 0 && netFlow !== 0) {
          hasMismatch = true;
          mismatchReason = 'Wallet activity exists but no GGR recorded';
        }

        // Calculate mismatch percentage relative to GGR (or deposits if no GGR)
        // This gives a better sense of significance than comparing to ending balance
        let mismatchPercent = 0;
        if (mismatchAmount !== 0) {
          // Use GGR as baseline if available, otherwise use total deposits
          const baseline = analyticsGGR !== 0 ? Math.abs(analyticsGGR) : 
                          totalDeposits !== 0 ? totalDeposits : 
                          Math.abs(expectedEndingBalance || 1);
          
          mismatchPercent = (Math.abs(mismatchAmount) / baseline) * 100;
        }

        return {
          player: player.username,
          playerName: player.name || player.username,
          analyticsGGR,
          analyticsNGR,
          analyticsTurnover,
          totalDeposits,
          totalWithdrawals,
          netFlow,
          startingBalance,
          walletEndingBalance: endingBalance, // Balance from last wallet transaction
          actualEndingBalance, // Balance at end of selected period (from last transaction)
          expectedEndingBalance,
          currentBalance, // Today's balance (for reference only, not used in comparison)
          expectedBalanceChange,
          actualBalanceChange,
          hasMismatch,
          mismatchAmount,
          mismatchPercent,
          mismatchReason,
          transactionCount: playerTransactions.length,
          analyticsRecordCount: playerAnalyticsData.length,
          analyticsData: playerAnalyticsData // Store for deep dive analysis
        };
      });

      // Wait for all player analysis to complete (includes async game transaction fetching)
      const playerAnalysis = await Promise.all(playerAnalysisPromises);

      // Calculate summary
      const summary = {
        totalPlayers: allPlayers.length,
        playersWithMismatches: playerAnalysis.filter(p => p.hasMismatch).length,
        totalAnalyticsGGR: playerAnalysis.reduce((sum, p) => sum + p.analyticsGGR, 0),
        totalNetFlow: playerAnalysis.reduce((sum, p) => sum + p.netFlow, 0),
        totalMismatchAmount: playerAnalysis.reduce((sum, p) => sum + Math.abs(p.mismatchAmount), 0),
        selectedUser: selectedUsers[0],
        dateRange: { startDate, endDate },
        currency
      };

      const results = {
        summary,
        playerAnalysis: playerAnalysis.sort((a, b) => {
          // Sort by: mismatches first, then by mismatch amount
          if (a.hasMismatch && !b.hasMismatch) return -1;
          if (!a.hasMismatch && b.hasMismatch) return 1;
          return Math.abs(b.mismatchAmount) - Math.abs(a.mismatchAmount);
        })
      };

      console.log('[GGR Analysis] Analysis complete:');
      console.log('  - Total Players:', summary.totalPlayers);
      console.log('  - Total Analytics GGR:', summary.totalAnalyticsGGR);
      console.log('  - Total Net Flow:', summary.totalNetFlow);
      console.log('  - Players with Mismatches:', summary.playersWithMismatches);
      console.log('  - Full Results:', results);
      
      setGGRAnalysisResults(results);
      setShowGGRAnalysisModal(true);

    } catch (error) {
      console.error('[GGR Analysis] Error:', error);
      setError(`GGR Analysis failed: ${error.message}`);
    } finally {
      setGGRAnalysisLoading(false);
    }
  };

  // Reset custom dates when switching away from custom range
  const handleDateRangeChange = (newRange) => {
    setDateRange(newRange);
    if (newRange !== 'custom') {
      setCustomDates({ startDate: null, endDate: null });
    }
  };

  // Validate custom dates
  const validateCustomDates = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const now = new Date();
    
    // Check if dates are valid
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return { isValid: false, error: 'Invalid date format' };
    }
    
    // Check if start date is before end date
    if (start >= end) {
      return { isValid: false, error: 'Start date must be before end date' };
    }
    
    // Check if end date is not in the future
    if (end > now) {
      return { isValid: false, error: 'End date cannot be in the future' };
    }
    
    // Check if date range is not too long (e.g., more than 1 year)
    const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    if (daysDiff > 365) {
      return { isValid: false, error: 'Date range cannot exceed 1 year' };
    }
    
    return { isValid: true };
  };

  // Get selection info for display
  const selectionInfo = useMemo(() => {
    return getSelectionDisplayInfo(selectedUsers);
  }, [selectedUsers]);

  // Toggle expanded state for a report
  const toggleReportExpanded = (username) => {
    const newExpanded = new Set(expandedReports);
    if (newExpanded.has(username)) {
      newExpanded.delete(username);
    } else {
      newExpanded.add(username);
    }
    setExpandedReports(newExpanded);
  };

  // Calculate summary metrics
  const summaryMetrics = useMemo(() => {
    if (analyticsData.length === 0) {
      // Return zeros when no data
      return {
        ggr: 0,
        ngr: 0,
        turnover: 0,
        casinoGGR: 0,
        casinoNGR: 0,
        casinoTurnover: 0,
        sportGGR: 0,
        sportNGR: 0,
        sportTurnover: 0,
        latestBalance: 0,
        balanceChange: 0,
        avgGGR: 0,
        avgNGR: 0,
        avgTurnover: 0,
        casinoAvgGGR: 0,
        sportAvgGGR: 0,
        activePlayers: effectivePlayers.length
      };
    }

    // Calculate totals and platform-specific metrics
    const totals = analyticsData.reduce((acc, day) => {
      // Overall totals
      acc.ggr += day.ggr;
      acc.ngr += day.ngr;
      acc.turnover += day.turnover;
      
      // Platform-specific totals (using pre-aggregated data)
      acc.casinoGGR += day.casinoGGR || 0;
      acc.casinoNGR += day.casinoNGR || 0;
      acc.casinoTurnover += day.casinoTurnover || 0;
      acc.sportGGR += day.sportGGR || 0;
      acc.sportNGR += day.sportNGR || 0;
      acc.sportTurnover += day.sportTurnover || 0;
      
      return acc;
    }, { 
      ggr: 0, 
      ngr: 0, 
      turnover: 0, 
      casinoGGR: 0,
      casinoNGR: 0,
      casinoTurnover: 0,
      sportGGR: 0,
      sportNGR: 0,
      sportTurnover: 0,
      activePlayers: effectivePlayers.length 
    });

    // Find the latest balance (last non-zero balance)
    let latestBalance = 0;
    for (let i = analyticsData.length - 1; i >= 0; i--) {
      if (analyticsData[i].balance > 0) {
        latestBalance = analyticsData[i].balance;
        break;
      }
    }
    
    // Find the earliest balance (first non-zero balance)
    let previousBalance = 0;
    for (let i = 0; i < analyticsData.length; i++) {
      if (analyticsData[i].balance > 0) {
        previousBalance = analyticsData[i].balance;
        break;
      }
    }
    
    const balanceChange = previousBalance !== 0 ? ((latestBalance - previousBalance) / previousBalance) * 100 : 0;

    return {
      ...totals,
      latestBalance,
      balanceChange,
      avgGGR: analyticsData.length > 0 ? totals.ggr / analyticsData.length : 0,
      avgNGR: analyticsData.length > 0 ? totals.ngr / analyticsData.length : 0,
      avgTurnover: analyticsData.length > 0 ? totals.turnover / analyticsData.length : 0,
      casinoAvgGGR: analyticsData.length > 0 ? totals.casinoGGR / analyticsData.length : 0,
      sportAvgGGR: analyticsData.length > 0 ? totals.sportGGR / analyticsData.length : 0
    };
  }, [analyticsData, effectivePlayers]);



  // Prepare chart data
  const chartData = useMemo(() => {
    // Determine if we should show hourly view
    let isHourly = dateRange === 'last7' || dateRange === 'today';
    
    // For custom dates, show hourly view if range is 7 days or less
    if (dateRange === 'custom' && customDates.startDate && customDates.endDate) {
      const daysDiff = Math.ceil((customDates.endDate - customDates.startDate) / (1000 * 60 * 60 * 24));
      isHourly = daysDiff <= 7;
    }
    
    const isMobile = window.innerWidth < 640;
    
    let cumulativeGGR = 0;
    let cumulativeNGR = 0;
    let cumulativeTurnover = 0;
    let cumulativeCasinoGGR = 0;
    let cumulativeCasinoNGR = 0;
    let cumulativeCasinoTurnover = 0;
    let cumulativeSportGGR = 0;
    let cumulativeSportNGR = 0;
    let cumulativeSportTurnover = 0;
    
    return analyticsData.map(data => {
      // Add to cumulative totals - ensure values are numbers
      const ggr = parseFloat(data.ggr) || 0;
      const ngr = parseFloat(data.ngr) || 0;
      const turnover = parseFloat(data.turnover) || 0;
      const casinoGGR = parseFloat(data.casinoGGR) || 0;
      const casinoNGR = parseFloat(data.casinoNGR) || 0;
      const casinoTurnover = parseFloat(data.casinoTurnover) || 0;
      const sportGGR = parseFloat(data.sportGGR) || 0;
      const sportNGR = parseFloat(data.sportNGR) || 0;
      const sportTurnover = parseFloat(data.sportTurnover) || 0;
      
      cumulativeGGR += ggr;
      cumulativeNGR += ngr;
      cumulativeTurnover += turnover;
      cumulativeCasinoGGR += casinoGGR;
      cumulativeCasinoNGR += casinoNGR;
      cumulativeCasinoTurnover += casinoTurnover;
      cumulativeSportGGR += sportGGR;
      cumulativeSportNGR += sportNGR;
      cumulativeSportTurnover += sportTurnover;
      
      const dateObj = data.dateObj || new Date(data.date);
      
      return {
        date: isHourly 
          ? (isMobile 
            ? formatInTimeZone(dateObj, BEIRUT_TIMEZONE, 'd/M H:00') 
            : formatInTimeZone(dateObj, BEIRUT_TIMEZONE, 'MMM d, HH:00'))
          : (isMobile 
            ? formatInTimeZone(dateObj, BEIRUT_TIMEZONE, 'd/M') 
            : formatInTimeZone(dateObj, BEIRUT_TIMEZONE, 'MMM d')),
        fullDate: isHourly 
          ? formatInTimeZone(dateObj, BEIRUT_TIMEZONE, 'MMM d, yyyy HH:00 zzz')
          : formatInTimeZone(dateObj, BEIRUT_TIMEZONE, 'MMM d, yyyy zzz'),
        // Daily values
        ggr: ggr,
        ngr: ngr,
        turnover: turnover,
        balance: data.balance,
        // Cumulative values
        cumulativeGGR: cumulativeGGR,
        cumulativeNGR: cumulativeNGR,
        cumulativeTurnover: cumulativeTurnover,
        // Platform-specific daily data for chart
        casinoGgr: showCasinoMetrics ? casinoGGR : null,
        casinoNgr: showCasinoMetrics ? casinoNGR : null,
        casinoTurnover: showCasinoMetrics ? casinoTurnover : null,
        sportGgr: showSportMetrics ? sportGGR : null,
        sportNgr: showSportMetrics ? sportNGR : null,
        sportTurnover: showSportMetrics ? sportTurnover : null,
        // Platform-specific cumulative data
        cumulativeCasinoGgr: showCasinoMetrics ? cumulativeCasinoGGR : null,
        cumulativeCasinoNgr: showCasinoMetrics ? cumulativeCasinoNGR : null,
        cumulativeCasinoTurnover: showCasinoMetrics ? cumulativeCasinoTurnover : null,
        cumulativeSportGgr: showSportMetrics ? cumulativeSportGGR : null,
        cumulativeSportNgr: showSportMetrics ? cumulativeSportNGR : null,
        cumulativeSportTurnover: showSportMetrics ? cumulativeSportTurnover : null
      };
    });
  }, [analyticsData, dateRange, showCasinoMetrics, showSportMetrics]);

  // Show breakglass warning if active
  if (isBreakglassActive) {
    return (
      <div className="space-y-6 p-4 max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
            {t('navigation.reports', 'Analytics Reports')}
          </h1>
        </div>
        
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-300 dark:border-yellow-700 rounded-xl p-8 text-center">
          <div className="flex flex-col items-center gap-4">
            <svg className="w-16 h-16 text-yellow-600 dark:text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {t('reports.breakglassMode', 'Breakglass Mode Active')}
              </h2>
              <p className="text-lg text-gray-700 dark:text-gray-300">
                {t('reports.breakglassModeDescription', 'Analytics reports are temporarily disabled for security reasons.')}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 max-w-7xl mx-auto">
      {/* System Maintenance Notice Bar */}
      {/* <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-2 border-blue-300 dark:border-blue-600 rounded-xl p-5 mb-6 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0">
            <svg className="w-7 h-7 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <div className="flex-1">
            <p className="text-base font-semibold text-blue-800 dark:text-blue-200">
              {t('reports.systemUpgrade.title', 'System Maintenance - Performance Upgrades')}
            </p>
            <p className="text-sm text-blue-700 dark:text-blue-300 mt-0.5">
              {t('reports.systemUpgrade.description', 'Some data may be delayed temporarily.')}
            </p>
          </div>
        </div>
      </div> */}
      {/* Floating action buttons */}
      <div className="fixed top-18 right-8 z-[100] flex flex-col gap-3">
        {/* Refresh button */}
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="inline-flex items-center gap-2 px-3 py-2 md:px-4 bg-white dark:bg-gray-800 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 ring-1 ring-gray-200 dark:ring-gray-700 hover:ring-gray-300 dark:hover:ring-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          title={t('reports.refreshData', 'Refresh analytics data')}
        >
          <ArrowPathIcon 
            className={`w-5 h-5 text-gray-600 dark:text-gray-300 ${isRefreshing ? 'animate-spin' : ''}`}
          />
          <span className="font-medium text-sm md:text-base text-gray-700 dark:text-gray-300">
            {t('common.refresh', 'Refresh')}
          </span>
        </button>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
          {t('navigation.reports', 'Analytics Reports')}
        </h1>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-red-900 dark:text-red-100">Error loading analytics</p>
              <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-medium text-gray-700 dark:text-gray-200">Filters</h2>
          {selectedUsers.length > 0 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  navigate(`/users/${selectedUsers[0].username}/deep-view`);
                }}
                className="text-xs px-3 py-1.5 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-all duration-200 flex items-center gap-1.5"
                title={`View detailed profile of ${selectedUsers[0].name || selectedUsers[0].username}`}
              >
                <UserIcon className="w-3.5 h-3.5" />
                <span>View Profile</span>
              </button>
              <button
                onClick={() => {
                  // Clear selection and increment request ID to invalidate pending requests
                  setSelectedUsers([]);
                  setAnalyticsData([]);
                  setDirectReportsData([]);
                  setDirectPlayerAnalytics(null);
                  setLastFetchParams(null);
                  currentRequestIdRef.current = currentRequestIdRef.current + 1;
                }}
                className="text-xs px-3 py-1.5 bg-gray-50 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 rounded-lg transition-all duration-200"
              >
                Clear
              </button>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <UserSelector 
            selectedUsers={selectedUsers}
            onUsersChange={setSelectedUsers}
            userTree={userTree}
          />
          
          <div className="space-y-4">
            {/* Date Range Picker - Full Width */}
            <div className="space-y-2 w-full">
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                Date Range
              </label>
              <DateRangePicker 
                value={dateRange}
                onChange={handleDateRangeChange}
                onCustomDatesChange={setCustomDates}
              />
            </div>
            
            {/* Currency Selector */}
            <div className="space-y-2">
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                Currency
              </label>
              
              {/* Scrollable Currency Container */}
              <div className="relative">
                <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent">
                  <div className="flex gap-2 pb-2 min-w-max">
                    {(() => {
                      // Priority order for currencies
                      const priorityOrder = ['LBP', 'USD', 'TND', 'TRY', 'IQD', 'KWD'];
                      
                      // Sort currencies based on priority order
                      const sortedCurrencies = [...availableCurrencies].sort((a, b) => {
                        const aIndex = priorityOrder.indexOf(a);
                        const bIndex = priorityOrder.indexOf(b);
                        
                        // If both are in priority list, sort by priority
                        if (aIndex !== -1 && bIndex !== -1) {
                          return aIndex - bIndex;
                        }
                        // If only 'a' is in priority list, it comes first
                        if (aIndex !== -1) return -1;
                        // If only 'b' is in priority list, it comes first
                        if (bIndex !== -1) return 1;
                        // Otherwise, alphabetical order
                        return a.localeCompare(b);
                      });
                      
                      return sortedCurrencies.map(curr => (
                        <button
                          key={curr}
                          onClick={() => setCurrency(curr)}
                          className={`
                            relative px-4 py-2 text-sm font-medium rounded-lg
                            transition-all duration-200 transform active:scale-95
                            whitespace-nowrap min-w-[70px]
                            ${currency === curr
                              ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-2 border-gray-300 dark:border-gray-500 shadow-sm'
                              : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-750 hover:text-gray-800 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500'
                            }
                          `}
                        >
                          <span className="flex items-center justify-center gap-1.5">
                            {/* Currency Code */}
                            <span className={`font-semibold tracking-wide ${currency === curr ? 'text-gray-900 dark:text-gray-100' : ''}`}>
                              {curr}
                            </span>
                            
                            {/* Selected Indicator - Subtle dot */}
                            {currency === curr && (
                              <div className="w-1.5 h-1.5 rounded-full bg-primary-500 dark:bg-primary-400"></div>
                            )}
                          </span>
                        </button>
                      ));
                    })()}
                  </div>
                </div>
                
                {/* Scroll Indicators */}
                {availableCurrencies.length > 5 && (
                  <>
                    <div className="absolute left-0 top-0 bottom-2 w-8 bg-gradient-to-r from-white dark:from-gray-800 to-transparent pointer-events-none" />
                    <div className="absolute right-0 top-0 bottom-2 w-8 bg-gradient-to-l from-white dark:from-gray-800 to-transparent pointer-events-none" />
                  </>
                )}
              </div>
              

            </div>
          </div>
        </div>

        {/* Selection Info */}
        {selectedUsers.length > 0 && (
          <div className="text-xs text-gray-500 mt-1 mb-2">
            {selectedUsers[0]?.type === 'player'
              ? `Analytics for player: ${selectedUsers[0]?.name || selectedUsers[0]?.username}`
              : `Aggregated analytics for ${selectionInfo.totalPlayers} player${selectionInfo.totalPlayers === 1 ? '' : 's'} under selected agent${selectionInfo.agents > 1 ? 's' : ''}`}
          </div>
        )}
      </div>

      {/* Content */}
      {selectedUsers.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-12 text-center">
          <UserIcon className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            Select User to View Analytics
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto">
            Choose a player to view their individual analytics, or select an agent to see aggregated data from all players under them.
          </p>
          <div className="mt-4 text-xs text-gray-400 dark:text-gray-500">
            Note: Analytics data (GGR, NGR, Turnover) is only available at the player level and will be aggregated for higher-level selections.
          </div>
        </div>
      ) : effectivePlayers.length === 0 ? (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-8 text-center">
          <UserGroupIcon className="w-12 h-12 text-yellow-600 dark:text-yellow-400 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-yellow-900 dark:text-yellow-100 mb-2">
            No Players Found
          </h3>
          <p className="text-sm text-yellow-700 dark:text-yellow-300">
            The selected {selectionInfo.agents === 1 ? 'agent has' : 'agents have'} no players underneath. 
            Analytics data is only available for players.
          </p>
        </div>
      ) : loading ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-primary-500 mx-auto mb-4"></div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Loading analytics data for {effectivePlayers.length} {effectivePlayers.length === 1 ? 'player' : 'players'}...
          </p>
        </div>
      ) : (
        <>
          {/* Summary Cards - Mobile Optimized */}
          {selectedUsers.length > 0 && (
            <div className="space-y-4">
              {/* Dynamic Metrics Hero Card */}
              <div className={`rounded-2xl p-0.5 shadow-xl transition-all duration-300 ${
                selectedHeroMetric === 'ggr' ? 'bg-gradient-to-br from-emerald-500 via-green-500 to-teal-500' :
                selectedHeroMetric === 'ngr' ? 'bg-gradient-to-br from-blue-500 via-sky-500 to-cyan-500' :
                selectedHeroMetric === 'turnover' ? 'bg-gradient-to-br from-purple-500 via-violet-500 to-indigo-500' :
                'bg-gradient-to-br from-orange-500 via-amber-500 to-yellow-500'
              }`}>
                <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 relative">
                  {/* Floating Icon */}
                  <div className="absolute -top-6 left-1/2 transform -translate-x-1/2">
                    <div className={`p-3 rounded-xl shadow-lg ${
                      selectedHeroMetric === 'ggr' ? 'bg-gradient-to-br from-emerald-500 to-green-500' :
                      selectedHeroMetric === 'ngr' ? 'bg-gradient-to-br from-blue-500 to-sky-500' :
                      selectedHeroMetric === 'turnover' ? 'bg-gradient-to-br from-purple-500 to-violet-500' :
                      'bg-gradient-to-br from-orange-500 to-amber-500'
                    }`}>
                      {selectedHeroMetric === 'ggr' && <CurrencyDollarIcon className="w-6 h-6 text-white" />}
                      {selectedHeroMetric === 'ngr' && <CurrencyDollarIcon className="w-6 h-6 text-white" />}
                      {selectedHeroMetric === 'turnover' && <ChartBarIcon className="w-6 h-6 text-white" />}
                      {selectedHeroMetric === 'balance' && <BanknotesIcon className="w-6 h-6 text-white" />}
                    </div>
                  </div>

                  {/* Cumulative Toggle - Top Right Corner */}
                  {selectedHeroMetric !== 'balance' && (
                    <div className="absolute top-2 right-2 sm:top-4 sm:right-4">
                      <button
                        onClick={() => setShowCumulative(!showCumulative)}
                        className={`flex items-center justify-center w-8 h-8 sm:w-auto sm:h-auto sm:px-2.5 sm:py-1 text-xs font-medium rounded-full transition-all shadow-sm ${
                          showCumulative
                            ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                            : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                        }`}
                        title={showCumulative ? 'Switch to daily view' : 'Switch to cumulative view'}
                      >
                        <svg className="w-3.5 h-3.5 sm:w-3 sm:h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          {showCumulative ? (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          ) : (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                          )}
                        </svg>
                        <span className="hidden lg:inline ml-1">{showCumulative ? 'Cumulative' : 'Daily'}</span>
                      </button>
                    </div>
                  )}

                  {/* Metric Selector */}
                  <div className="flex items-center justify-center mb-4 mt-2">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setSelectedHeroMetric('ggr')}
                        className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all whitespace-nowrap ${
                          selectedHeroMetric === 'ggr'
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                            : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                        }`}
                      >
                        GGR
                      </button>
                      <button
                        onClick={() => setSelectedHeroMetric('ngr')}
                        className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all whitespace-nowrap ${
                          selectedHeroMetric === 'ngr'
                            ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                            : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                        }`}
                      >
                        NGR
                      </button>
                      <button
                        onClick={() => setSelectedHeroMetric('turnover')}
                        className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all whitespace-nowrap ${
                          selectedHeroMetric === 'turnover'
                            ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                            : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                        }`}
                      >
                        Turnover
                      </button>
                      <button
                        onClick={() => setSelectedHeroMetric('balance')}
                        className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all whitespace-nowrap ${
                          selectedHeroMetric === 'balance'
                            ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300'
                            : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                        }`}
                      >
                        Balance
                      </button>
                    </div>
                  </div>

                  <div className="mb-2">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                      {selectedHeroMetric === 'ggr' ? 'Gross Gaming Revenue' :
                       selectedHeroMetric === 'ngr' ? 'Net Gaming Revenue' :
                       selectedHeroMetric === 'turnover' ? 'Total Turnover' :
                       'Current Balance'}
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {selectedHeroMetric === 'balance' ? 'Latest balance' : 'Total for selected period'}
                    </p>
                  </div>
                  
                  {/* Dynamic Value Display */}
                  {(loading || loadingDirectReports || loadingDirectPlayers) ? (
                    <div className="text-center py-6 border-b border-gray-200 dark:border-gray-700">
                      <div className="animate-spin rounded-full h-10 w-10 border-4 border-gray-200 border-t-primary-500 mx-auto mb-3"></div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Calculating metrics...
                      </p>
                    </div>
                  ) : (
                  <div className="text-center py-6 border-b border-gray-200 dark:border-gray-700">
                    <div className={`text-3xl md:text-4xl font-semibold mb-2`}>
                      <span className={`px-3 py-1 rounded-lg ${
                        selectedHeroMetric === 'ggr' ? (
                          summaryMetrics.ggr > 0 ? 'bg-green-50 dark:bg-green-900/20' : 
                          summaryMetrics.ggr < 0 ? 'bg-red-50 dark:bg-red-900/20' : ''
                        ) :
                        selectedHeroMetric === 'ngr' ? (
                          summaryMetrics.ngr > 0 ? 'bg-blue-50 dark:bg-blue-900/20' : 
                          summaryMetrics.ngr < 0 ? 'bg-red-50 dark:bg-red-900/20' : ''
                        ) :
                        selectedHeroMetric === 'turnover' ? 'bg-purple-50 dark:bg-purple-900/20' :
                        'bg-orange-50 dark:bg-orange-900/20'
                      }`}>
                        {selectedHeroMetric === 'ggr' ? formatCurrency(summaryMetrics.ggr, currency) :
                         selectedHeroMetric === 'ngr' ? formatCurrency(summaryMetrics.ngr, currency) :
                         selectedHeroMetric === 'turnover' ? formatCurrency(summaryMetrics.turnover, currency) :
                         formatCurrency(summaryMetrics.latestBalance, currency)}
                      </span>
                    </div>
                    <div className="flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                      <span>Daily Average:</span>
                      <span className="font-semibold text-gray-700 dark:text-gray-300">
                        {selectedHeroMetric === 'ggr' ? formatCurrency(summaryMetrics.avgGGR, currency) :
                         selectedHeroMetric === 'ngr' ? formatCurrency(summaryMetrics.avgNGR, currency) :
                         selectedHeroMetric === 'turnover' ? formatCurrency(summaryMetrics.avgTurnover, currency) :
                         `${summaryMetrics.balanceChange >= 0 ? '+' : ''}${summaryMetrics.balanceChange.toFixed(1)}% change`}
                      </span>
                    </div>
                  </div>
                  )}
                  
                  {/* Enhanced Mini Chart */}
                  {!(loading || loadingDirectReports || loadingDirectPlayers) && (
                  <div className="mt-4">
                    {(() => {
                      const metricKey = showCumulative && selectedHeroMetric !== 'balance' 
                        ? `cumulative${selectedHeroMetric.toUpperCase()}` 
                        : selectedHeroMetric;
                      const metricData = chartData.map(d => d[metricKey]);
                      const maxValue = Math.max(...metricData);
                      const minValue = Math.min(...metricData);
                      const maxIndex = metricData.indexOf(maxValue);
                      const minIndex = metricData.indexOf(minValue);
                      const strokeColor = selectedHeroMetric === 'ggr' ? (summaryMetrics.ggr >= 0 ? "rgb(34, 197, 94)" : "rgb(239, 68, 68)") :
                                         selectedHeroMetric === 'ngr' ? (summaryMetrics.ngr >= 0 ? "rgb(59, 130, 246)" : "rgb(239, 68, 68)") :
                                         selectedHeroMetric === 'turnover' ? "rgb(168, 85, 247)" :
                                         "rgb(251, 146, 60)";
                      
                      return (
                        <>
                          <div className="h-24">
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={chartData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                                <defs>
                                  <linearGradient id={`${selectedHeroMetric}Gradient`} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={strokeColor} stopOpacity={0.3}/>
                                    <stop offset="95%" stopColor={strokeColor} stopOpacity={0}/>
                                  </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.3} vertical={false} />
                                <Line 
                                  type="monotone" 
                                  dataKey={metricKey}
                                  stroke={strokeColor}
                                  strokeWidth={2}
                                  dot={(props) => {
                                    const { index } = props;
                                    if (index === maxIndex || index === minIndex) {
                                      return (
                                        <circle
                                          cx={props.cx}
                                          cy={props.cy}
                                          r={4}
                                          fill={strokeColor}
                                          stroke="#fff"
                                          strokeWidth={2}
                                        />
                                      );
                                    }
                                    return null;
                                  }}
                                />
                                <Area
                                  type="monotone"
                                  dataKey={metricKey}
                                  stroke="none"
                                  fillOpacity={1}
                                  fill={`url(#${selectedHeroMetric}Gradient)`}
                                />
                                
                                {/* Casino metrics line */}
                                {showCasinoMetrics && (
                                  <Line 
                                    type="monotone" 
                                    dataKey={showCumulative && selectedHeroMetric !== 'balance' 
                                      ? `cumulativeCasino${selectedHeroMetric.charAt(0).toUpperCase() + selectedHeroMetric.slice(1).toLowerCase()}`
                                      : `casino${selectedHeroMetric.charAt(0).toUpperCase() + selectedHeroMetric.slice(1).toLowerCase()}`}
                                    stroke="#8b5cf6"
                                    strokeWidth={2}
                                    strokeDasharray="5 5"
                                    dot={false}
                                  />
                                )}
                                
                                {/* Sport metrics line */}
                                {showSportMetrics && (
                                  <Line 
                                    type="monotone" 
                                    dataKey={showCumulative && selectedHeroMetric !== 'balance' 
                                      ? `cumulativeSport${selectedHeroMetric.charAt(0).toUpperCase() + selectedHeroMetric.slice(1).toLowerCase()}`
                                      : `sport${selectedHeroMetric.charAt(0).toUpperCase() + selectedHeroMetric.slice(1).toLowerCase()}`}
                                    stroke="#10b981"
                                    strokeWidth={2}
                                    strokeDasharray="3 3"
                                    dot={false}
                                  />
                                )}
                                <Tooltip
                                  content={({ active, payload }) => {
                                    if (active && payload && payload.length) {
                                      return (
                                        <div className="bg-white dark:bg-gray-800 px-3 py-2 rounded shadow-lg border border-gray-200 dark:border-gray-700">
                                          <p className="text-xs text-gray-500 dark:text-gray-400">{payload[0].payload.fullDate}</p>
                                          {payload.map((entry, index) => (
                                            <div key={index} className="flex items-center gap-2">
                                              <div 
                                                className="w-3 h-3 rounded-full" 
                                                style={{ 
                                                  backgroundColor: entry.color || 
                                                    (entry.dataKey?.includes('casino') ? '#8b5cf6' : 
                                                     entry.dataKey?.includes('sport') ? '#10b981' : 
                                                     strokeColor) 
                                                }}
                                              />
                                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                                {entry.dataKey?.includes('cumulative') ? 'Cumulative ' : ''}
                                                {entry.dataKey?.includes('casino') ? 'Casino ' : 
                                                 entry.dataKey?.includes('sport') ? 'Sport ' : ''}
                                                {formatCurrency(entry.value, currency)}
                                              </p>
                                            </div>
                                          ))}
                                        </div>
                                      );
                                    }
                                    return null;
                                  }}
                                />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                          
                          {/* High/Low Indicators */}
                          <div className="flex justify-center gap-2 mt-3">
                            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg px-2 py-1 text-xs">
                              <div className="flex items-center gap-1">
                                <ArrowTrendingUpIcon className="w-2.5 h-2.5 text-green-500" />
                                <span className="text-gray-500 dark:text-gray-400">High:</span>
                                <span className="font-semibold text-gray-900 dark:text-white">
                                  {formatCurrency(maxValue, currency)}
                                </span>
                              </div>
                            </div>
                            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg px-2 py-1 text-xs">
                              <div className="flex items-center gap-1">
                                <ArrowTrendingDownIcon className="w-2.5 h-2.5 text-red-500" />
                                <span className="text-gray-500 dark:text-gray-400">Low:</span>
                                <span className="font-semibold text-gray-900 dark:text-white">
                                  {formatCurrency(minValue, currency)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                  )}
                  
                  {/* Platform Breakdown - Only show for GGR/NGR/Turnover */}
                  {selectedHeroMetric !== 'balance' && !(loading || loadingDirectReports || loadingDirectPlayers) && (
                    <div className="mt-4 space-y-3 text-sm">
                      <div 
                        className={`rounded-lg p-3 cursor-pointer transition-all duration-200 ${
                          showCasinoMetrics 
                            ? 'bg-purple-100 dark:bg-purple-800/40 ring-2 ring-purple-300 dark:ring-purple-600' 
                            : 'bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-800/30'
                        }`}
                        onClick={() => setShowCasinoMetrics(!showCasinoMetrics)}
                        title={showCasinoMetrics ? 'Remove casino metrics from graph' : 'Add casino metrics to graph'}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-lg">🎰</span>
                          <span className="font-medium text-purple-700 dark:text-purple-300">Casino</span>
                          {showCasinoMetrics && (
                            <span className="text-xs px-1.5 py-0.5 bg-purple-200 dark:bg-purple-700 text-purple-700 dark:text-purple-300 rounded-full">
                              Graphed
                            </span>
                          )}
                        </div>
                        <div className="text-lg font-bold text-purple-900 dark:text-purple-100">
                          {selectedHeroMetric === 'ggr' ? formatCurrency(summaryMetrics.casinoGGR || 0, currency) :
                           selectedHeroMetric === 'ngr' ? formatCurrency(summaryMetrics.casinoNGR || 0, currency) :
                           formatCurrency(summaryMetrics.casinoTurnover || 0, currency)}
                        </div>
                      </div>
                      <div 
                        className={`rounded-lg p-3 cursor-pointer transition-all duration-200 ${
                          showSportMetrics 
                            ? 'bg-green-100 dark:bg-green-800/40 ring-2 ring-green-300 dark:ring-green-600' 
                            : 'bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-800/30'
                        }`}
                        onClick={() => setShowSportMetrics(!showSportMetrics)}
                        title={showSportMetrics ? 'Remove sport metrics from graph' : 'Add sport metrics to graph'}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-lg">⚽</span>
                          <span className="font-medium text-green-700 dark:text-green-300">Sport</span>
                          {showSportMetrics && (
                            <span className="text-xs px-1.5 py-0.5 bg-green-200 dark:bg-green-700 text-green-700 dark:text-green-300 rounded-full">
                              Graphed
                            </span>
                          )}
                        </div>
                        <div className="text-lg font-bold text-green-900 dark:text-green-100">
                          {selectedHeroMetric === 'ggr' ? formatCurrency(summaryMetrics.sportGGR || 0, currency) :
                           selectedHeroMetric === 'ngr' ? formatCurrency(summaryMetrics.sportNGR || 0, currency) :
                           formatCurrency(summaryMetrics.sportTurnover || 0, currency)}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Expandable Metrics Section */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700">
                <button
                  onClick={() => setExpandedMetrics(!expandedMetrics)}
                  className="w-full p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors rounded-2xl"
                >
                  <div className="flex items-center gap-3">
                    <ChartBarIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    <span className="font-medium text-gray-900 dark:text-white">
                      {expandedMetrics ? 'Hide Details' : 'View All Metrics'}
                    </span>
                  </div>
                  <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform ${expandedMetrics ? 'rotate-180' : ''}`} />
                </button>
                
                {expandedMetrics && (
                  <div className="border-t border-gray-200 dark:border-gray-700 p-4 space-y-4">
                    {(loading || loadingDirectReports || loadingDirectPlayers) ? (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-10 w-10 border-4 border-gray-200 border-t-primary-500 mx-auto mb-3"></div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Loading detailed metrics...
                        </p>
                      </div>
                    ) : (
                      <>
                    {/* Key Metrics Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      {/* NGR Card */}
                      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-blue-700 dark:text-blue-300">Net Gaming Revenue</span>
                          <CurrencyDollarIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="space-y-1">
                          <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                            {formatCurrency(summaryMetrics.ngr, currency)}
                          </p>
                          <p className="text-xs text-blue-600 dark:text-blue-400">
                            Daily: {formatCurrency(summaryMetrics.avgNGR, currency)}
                          </p>
                        </div>
                      </div>
                      
                      {/* Turnover Card */}
                      <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-purple-700 dark:text-purple-300">Total Turnover</span>
                          <ChartBarIcon className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                        </div>
                        <div className="space-y-1">
                          <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">
                            {formatCurrency(summaryMetrics.turnover, currency)}
                          </p>
                          <p className="text-xs text-purple-600 dark:text-purple-400">
                            Daily: {formatCurrency(summaryMetrics.avgTurnover, currency)}
                          </p>
                        </div>
                      </div>
                      
                      {/* Balance Card */}
                      <div className="bg-orange-50 dark:bg-orange-900/20 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-orange-700 dark:text-orange-300">Current Balance</span>
                          <BanknotesIcon className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                        </div>
                        <div className="space-y-1">
                          <p className="text-2xl font-bold text-orange-900 dark:text-orange-100">
                            {formatCurrency(summaryMetrics.latestBalance, currency)}
                          </p>
                          <p className={`text-xs flex items-center gap-1 ${
                            summaryMetrics.balanceChange >= 0 
                              ? 'text-green-600 dark:text-green-400' 
                              : 'text-red-600 dark:text-red-400'
                          }`}>
                            {summaryMetrics.balanceChange >= 0 ? (
                              <ArrowTrendingUpIcon className="w-3 h-3" />
                            ) : (
                              <ArrowTrendingDownIcon className="w-3 h-3" />
                            )}
                            {Math.abs(summaryMetrics.balanceChange).toFixed(1)}% change
                          </p>
                        </div>
                      </div>
                      
                      {/* Active Players Card */}
                      <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300">Active Players</span>
                          <UserGroupIcon className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                        </div>
                        <div className="space-y-1">
                          <p className="text-2xl font-bold text-indigo-900 dark:text-indigo-100">
                            {summaryMetrics.activePlayers}
                          </p>
                          <p className="text-xs text-indigo-600 dark:text-indigo-400">
                            In selected period
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Platform Comparison */}
                    <div className="space-y-4">
                      {/* Casino Detailed */}
                      <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-4">
                          <span className="text-2xl">🎰</span>
                          <h3 className="text-lg font-bold text-gray-900 dark:text-white">Casino Analytics</h3>
                        </div>
                        <div className="space-y-3">
                          <div className="flex justify-between items-start">
                            <span className="text-sm text-gray-600 dark:text-gray-400">GGR</span>
                            <div className="text-right">
                              <p className="text-lg font-bold text-gray-900 dark:text-white">
                                {formatCurrency(summaryMetrics.casinoGGR || 0, currency)}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {summaryMetrics.ggr > 0 
                                  ? `${((summaryMetrics.casinoGGR / summaryMetrics.ggr) * 100).toFixed(1)}% of total`
                                  : '0% of total'}
                              </p>
                            </div>
                          </div>
                          <div className="flex justify-between items-start">
                            <span className="text-sm text-gray-600 dark:text-gray-400">NGR</span>
                            <p className="text-lg font-bold text-gray-900 dark:text-white">
                              {formatCurrency(summaryMetrics.casinoNGR || 0, currency)}
                            </p>
                          </div>
                          <div className="flex justify-between items-start">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Turnover</span>
                            <p className="text-lg font-bold text-gray-900 dark:text-white">
                              {formatCurrency(summaryMetrics.casinoTurnover || 0, currency)}
                            </p>
                          </div>
                          <div className="pt-3 border-t border-purple-200 dark:border-purple-800/30">
                            <div className="flex justify-between items-center">
                              <span className="text-sm font-medium text-purple-700 dark:text-purple-300">Win Rate</span>
                              <span className="text-lg font-bold text-purple-600 dark:text-purple-400">
                                {summaryMetrics.casinoTurnover > 0 
                                  ? ((summaryMetrics.casinoGGR / summaryMetrics.casinoTurnover) * 100).toFixed(2) 
                                  : '0.00'}%
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Sport Detailed */}
                      <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-4">
                          <span className="text-2xl">⚽</span>
                          <h3 className="text-lg font-bold text-gray-900 dark:text-white">Sport Analytics</h3>
                        </div>
                        <div className="space-y-3">
                          <div className="flex justify-between items-start">
                            <span className="text-sm text-gray-600 dark:text-gray-400">GGR</span>
                            <div className="text-right">
                              <p className="text-lg font-bold text-gray-900 dark:text-white">
                                {formatCurrency(summaryMetrics.sportGGR || 0, currency)}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {summaryMetrics.ggr > 0 
                                  ? `${((summaryMetrics.sportGGR / summaryMetrics.ggr) * 100).toFixed(1)}% of total`
                                  : '0% of total'}
                              </p>
                            </div>
                          </div>
                          <div className="flex justify-between items-start">
                            <span className="text-sm text-gray-600 dark:text-gray-400">NGR</span>
                            <p className="text-lg font-bold text-gray-900 dark:text-white">
                              {formatCurrency(summaryMetrics.sportNGR || 0, currency)}
                            </p>
                          </div>
                          <div className="flex justify-between items-start">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Turnover</span>
                            <p className="text-lg font-bold text-gray-900 dark:text-white">
                              {formatCurrency(summaryMetrics.sportTurnover || 0, currency)}
                            </p>
                          </div>
                          <div className="pt-3 border-t border-green-200 dark:border-green-800/30">
                            <div className="flex justify-between items-center">
                              <span className="text-sm font-medium text-green-700 dark:text-green-300">Win Rate</span>
                              <span className="text-lg font-bold text-green-600 dark:text-green-400">
                                {summaryMetrics.sportTurnover > 0 
                                  ? ((summaryMetrics.sportGGR / summaryMetrics.sportTurnover) * 100).toFixed(2) 
                                  : '0.00'}%
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    

                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Direct Players Analytics for Super-Agents */}
          {selectedUsers.length === 1 && selectedUsers[0].type === 'super-agent' && (directPlayerAnalytics || loadingDirectPlayers) && (
            <div className="mt-6">
              <div className="bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 dark:from-amber-900/20 dark:via-yellow-900/20 dark:to-orange-900/20 rounded-2xl shadow-lg overflow-hidden border border-amber-200 dark:border-amber-800">
                {/* Header */}
                <div className="bg-gradient-to-r from-amber-100 to-yellow-100 dark:from-amber-900/30 dark:to-yellow-900/30 px-6 py-4 border-b border-amber-200 dark:border-amber-800">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-amber-200 dark:bg-amber-800 rounded-lg">
                        <UserIcon className="w-5 h-5 text-amber-700 dark:text-amber-300" />
                      </div>
                      <div>
                        <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100">
                          Direct Players Only
                        </h2>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Analytics for {directPlayerAnalytics.playerCount} {directPlayerAnalytics.playerCount === 1 ? 'player' : 'players'} directly under {selectedUsers[0].name}
                        </p>
                      </div>
                    </div>
                    <div className="text-xs px-3 py-1 bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200 rounded-full font-medium">
                      Excludes sub-agents
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  {loadingDirectPlayers ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-10 w-10 border-4 border-amber-200 border-t-amber-500 mx-auto mb-3"></div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Loading direct player analytics...
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Main Metrics */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        {/* GGR */}
                        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">GGR</span>
                            <CurrencyDollarIcon className="w-4 h-4 text-green-600 dark:text-green-400" />
                          </div>
                          <p className={`text-2xl font-bold ${
                            directPlayerAnalytics.totals.ggr >= 0 
                              ? 'text-green-700 dark:text-green-300' 
                              : 'text-red-700 dark:text-red-300'
                          }`}>
                            {formatCurrency(directPlayerAnalytics.totals.ggr, currency)}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {summaryMetrics.ggr !== 0 
                              ? `${((directPlayerAnalytics.totals.ggr / summaryMetrics.ggr) * 100).toFixed(1)}% of total`
                              : 'N/A'}
                          </p>
                        </div>

                        {/* NGR */}
                        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">NGR</span>
                            <CurrencyDollarIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                          </div>
                          <p className={`text-2xl font-bold ${
                            directPlayerAnalytics.totals.ngr >= 0 
                              ? 'text-blue-700 dark:text-blue-300' 
                              : 'text-red-700 dark:text-red-300'
                          }`}>
                            {formatCurrency(directPlayerAnalytics.totals.ngr, currency)}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {summaryMetrics.ngr !== 0 
                              ? `${((directPlayerAnalytics.totals.ngr / summaryMetrics.ngr) * 100).toFixed(1)}% of total`
                              : 'N/A'}
                          </p>
                        </div>

                        {/* Turnover */}
                        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Turnover</span>
                            <ChartBarIcon className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                          </div>
                          <p className="text-2xl font-bold text-purple-700 dark:text-purple-300">
                            {formatCurrency(directPlayerAnalytics.totals.turnover, currency)}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {summaryMetrics.turnover !== 0 
                              ? `${((directPlayerAnalytics.totals.turnover / summaryMetrics.turnover) * 100).toFixed(1)}% of total`
                              : 'N/A'}
                          </p>
                        </div>
                      </div>

                      {/* Platform Breakdown */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Casino */}
                        <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="text-xl">🎰</span>
                            <h3 className="font-medium text-gray-900 dark:text-white">Casino</h3>
                          </div>
                          <div className="space-y-2">
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600 dark:text-gray-400">GGR</span>
                              <span className="text-sm font-bold text-gray-900 dark:text-white">
                                {formatCurrency(directPlayerAnalytics.totals.casinoGGR, currency)}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600 dark:text-gray-400">NGR</span>
                              <span className="text-sm font-bold text-gray-900 dark:text-white">
                                {formatCurrency(directPlayerAnalytics.totals.casinoNGR, currency)}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600 dark:text-gray-400">Turnover</span>
                              <span className="text-sm font-bold text-gray-900 dark:text-white">
                                {formatCurrency(directPlayerAnalytics.totals.casinoTurnover, currency)}
                              </span>
                            </div>
                            <div className="pt-2 mt-2 border-t border-purple-200 dark:border-purple-800/30">
                              <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-purple-700 dark:text-purple-300">Win Rate</span>
                                <span className="text-sm font-bold text-purple-600 dark:text-purple-400">
                                  {directPlayerAnalytics.totals.casinoTurnover > 0 
                                    ? ((directPlayerAnalytics.totals.casinoGGR / directPlayerAnalytics.totals.casinoTurnover) * 100).toFixed(2) 
                                    : '0.00'}%
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Sport */}
                        <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="text-xl">⚽</span>
                            <h3 className="font-medium text-gray-900 dark:text-white">Sport</h3>
                          </div>
                          <div className="space-y-2">
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600 dark:text-gray-400">GGR</span>
                              <span className="text-sm font-bold text-gray-900 dark:text-white">
                                {formatCurrency(directPlayerAnalytics.totals.sportGGR, currency)}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600 dark:text-gray-400">NGR</span>
                              <span className="text-sm font-bold text-gray-900 dark:text-white">
                                {formatCurrency(directPlayerAnalytics.totals.sportNGR, currency)}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600 dark:text-gray-400">Turnover</span>
                              <span className="text-sm font-bold text-gray-900 dark:text-white">
                                {formatCurrency(directPlayerAnalytics.totals.sportTurnover, currency)}
                              </span>
                            </div>
                            <div className="pt-2 mt-2 border-t border-green-200 dark:border-green-800/30">
                              <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-green-700 dark:text-green-300">Win Rate</span>
                                <span className="text-sm font-bold text-green-600 dark:text-green-400">
                                  {directPlayerAnalytics.totals.sportTurnover > 0 
                                    ? ((directPlayerAnalytics.totals.sportGGR / directPlayerAnalytics.totals.sportTurnover) * 100).toFixed(2) 
                                    : '0.00'}%
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Player List */}
                      {directPlayerAnalytics.players.length > 0 && (
                        <div className="mt-4 bg-white dark:bg-gray-800 rounded-xl p-4">
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Direct Players:
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {directPlayerAnalytics.players.map(player => (
                              <span 
                                key={player.username}
                                className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full"
                              >
                                {player.name || player.username}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}



          {/* Direct Reports Analytics */}
          {selectedUsers.length > 0 && getDirectReports().length > 0 && (
            <div className="mt-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100">
                  Direct Reports Performance
                </h2>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {getDirectReports().length} direct {getDirectReports().length === 1 ? 'report' : 'reports'}
                </span>
              </div>

              {loadingDirectReports ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-8 text-center">
                  <div className="animate-spin rounded-full h-10 w-10 border-4 border-gray-200 border-t-primary-500 mx-auto mb-3"></div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Loading direct reports analytics...
                  </p>
                </div>
              ) : (
                <>
                  {/* Filter info and toggle button for zero GGR reports */}
                  {(() => {
                    const nonZeroReports = directReportsData.filter(r => r.analytics && r.analytics.total.ggr !== 0);
                    const zeroReports = directReportsData.filter(r => !r.analytics || r.analytics.total.ggr === 0);
                    
                    return zeroReports.length > 0 ? (
                      <div className="mb-4 flex items-center justify-between bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            Showing {nonZeroReports.length} active {nonZeroReports.length === 1 ? 'report' : 'reports'}
                          </span>
                          {!showZeroGGRReports && (
                            <span className="text-xs px-2 py-1 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full">
                              {zeroReports.length} hidden (0 GGR)
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => setShowZeroGGRReports(!showZeroGGRReports)}
                          className="flex items-center gap-2 px-3 py-1.5 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                        >
                          {showZeroGGRReports ? (
                            <>
                              <ChevronUpIcon className="w-4 h-4" />
                              <span>Hide Zero GGR</span>
                            </>
                          ) : (
                            <>
                              <ChevronDownIcon className="w-4 h-4" />
                              <span>Show All ({zeroReports.length} with 0 GGR)</span>
                            </>
                          )}
                        </button>
                      </div>
                    ) : null;
                  })()}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 overflow-visible pt-3 pr-3">
                    {directReportsData
                      .filter(report => {
                        // Show report if showZeroGGRReports is true, or if it has non-zero GGR
                        if (showZeroGGRReports) return true;
                        return report.analytics && report.analytics.total.ggr !== 0;
                      })
                    .sort((a, b) => {
                      // Sort by total GGR descending (highest first)
                      const aGGR = a.analytics?.total?.ggr || 0;
                      const bGGR = b.analytics?.total?.ggr || 0;
                      return bGGR - aGGR;
                    })
                    .map((report, index) => {
                      const isExpanded = expandedReports.has(report.username);
                      return (
                    <div 
                      key={report.username} 
                      className={`relative ${
                        isExpanded ? 'col-span-full' : ''
                      }`}
                    >
                      {/* Ranking Badge */}
                      {report.analytics && (
                        <div className={`absolute left-1/2 transform -translate-x-1/2 z-50 ${
                          index === 0 ? '-top-5' : '-top-4'
                        }`}>
                          {index === 0 && (
                            <div className="relative group">
                              {/* Glow effect */}
                              <div className="absolute inset-0 bg-yellow-400 rounded-full blur-md opacity-75 group-hover:opacity-100 transition-opacity"></div>
                              {/* Main badge */}
                              <div className="relative bg-gradient-to-br from-yellow-300 via-yellow-400 to-amber-500 rounded-full w-10 h-10 flex items-center justify-center shadow-lg transform hover:scale-110 transition-transform">
                                <div className="absolute inset-0 rounded-full bg-gradient-to-t from-transparent via-transparent to-white opacity-30"></div>
                                <span className="text-lg font-bold text-yellow-900 relative z-10">1</span>
                              </div>
                            </div>
                          )}
                          {index === 1 && (
                            <div className="relative group">
                              {/* Glow effect */}
                              <div className="absolute inset-0 bg-gray-400 rounded-full blur-md opacity-75 group-hover:opacity-100 transition-opacity"></div>
                              {/* Main badge */}
                              <div className="relative bg-gradient-to-br from-gray-200 via-gray-300 to-gray-400 rounded-full w-9 h-9 flex items-center justify-center shadow-lg transform hover:scale-110 transition-transform">
                                <div className="absolute inset-0 rounded-full bg-gradient-to-t from-transparent via-transparent to-white opacity-30"></div>
                                <span className="text-base font-bold text-gray-700 relative z-10">2</span>
                              </div>
                            </div>
                          )}
                          {index === 2 && (
                            <div className="relative group">
                              {/* Glow effect */}
                              <div className="absolute inset-0 bg-orange-400 rounded-full blur-md opacity-75 group-hover:opacity-100 transition-opacity"></div>
                              {/* Main badge */}
                              <div className="relative bg-gradient-to-br from-orange-300 via-orange-400 to-orange-500 rounded-full w-8 h-8 flex items-center justify-center shadow-lg transform hover:scale-110 transition-transform">
                                <div className="absolute inset-0 rounded-full bg-gradient-to-t from-transparent via-transparent to-white opacity-30"></div>
                                <span className="text-sm font-bold text-orange-900 relative z-10">3</span>
                              </div>
                            </div>
                          )}
                          {index > 2 && (
                            <div className="relative">
                              {/* Regular badge for positions 4+ */}
                              <div className="bg-gray-100 dark:bg-gray-700 rounded-full w-7 h-7 flex items-center justify-center shadow-md">
                                <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">{index + 1}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Card Content */}
                      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 direct-report-card overflow-hidden"
                        style={{
                          boxShadow: report.analytics && report.analytics.total.ggr !== 0
                            ? report.analytics.total.ggr > 0
                              ? '0 0 0 2px rgba(74, 222, 128, 0.3), 0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                              : '0 0 0 2px rgba(248, 113, 113, 0.3), 0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                            : undefined
                        }}
                      >
                        {/* Header Section */}
                        <div className="bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 p-4 sm:p-5 rounded-t-2xl">
                          <div className="flex items-start justify-between gap-3 mb-4">
                                                          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                                <div 
                                  className={`p-2 sm:p-2.5 rounded-xl shadow-sm flex-shrink-0 cursor-pointer hover:scale-105 transition-transform ${
                                    report.type === 'agent' ? 'bg-gradient-to-br from-yellow-100 to-yellow-200 dark:from-yellow-900/30 dark:to-yellow-800/30' :
                                    report.type === 'super-agent' ? 'bg-gradient-to-br from-orange-100 to-orange-200 dark:from-orange-900/30 dark:to-orange-800/30' :
                                    report.type === 'city' ? 'bg-gradient-to-br from-green-100 to-green-200 dark:from-green-900/30 dark:to-green-800/30' :
                                    report.type === 'country' ? 'bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900/30 dark:to-blue-800/30' :
                                    'bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600'
                                  }`}
                                  onClick={() => {
                                    setSelectedUsers([{
                                      ...report,
                                      attributes: report.attributes || {},
                                      balances: report.balances || {}
                                    }]);
                                  }}
                                  title={`Click to view ${report.name}'s analytics`}
                                >
                                  {report.type === 'agent' && <UserGroupIcon className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-700 dark:text-yellow-400" />}
                                  {report.type === 'super-agent' && <StarIcon className="w-4 h-4 sm:w-5 sm:h-5 text-orange-700 dark:text-orange-400" />}
                                  {report.type === 'city' && <BuildingStorefrontIcon className="w-4 h-4 sm:w-5 sm:h-5 text-green-700 dark:text-green-400" />}
                                  {report.type === 'country' && <GlobeAsiaAustraliaIcon className="w-4 h-4 sm:w-5 sm:h-5 text-blue-700 dark:text-blue-400" />}
                                  {report.type === 'player' && <UserIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700 dark:text-gray-400" />}
                                </div>
                                <div 
                                  className="flex-1 min-w-0 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg p-1 -m-1 transition-colors"
                                  onClick={() => {
                                    setSelectedUsers([{
                                      ...report,
                                      attributes: report.attributes || {},
                                      balances: report.balances || {}
                                    }]);
                                  }}
                                  title={`Click to view ${report.name}'s analytics`}
                                >
                                  <h3 className="font-bold text-base sm:text-lg text-gray-900 dark:text-white truncate">
                                    {report.name}
                                  </h3>
                                  <div className="flex flex-wrap items-center gap-1 sm:gap-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                                    <span className="truncate">@{report.username}</span>
                                    <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded-full capitalize whitespace-nowrap">
                                      {report.type.replace('-', ' ')}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            {report.playerCount > 0 && (
                              <div className="flex flex-col items-end flex-shrink-0">
                                <span className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                                  {report.playerCount}
                                </span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {report.playerCount === 1 ? 'player' : 'players'}
                                </span>
                              </div>
                            )}
                          </div>
                          
                          {/* Summary Metrics */}
                          {report.analytics ? (
                            !isExpanded ? (
                              // Collapsed view - only show GGR
                              <div className="flex items-center justify-between p-3 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700/50 dark:to-gray-800/50 rounded-xl">
                                <div className="flex items-center gap-2">
                                  <ArrowTrendingUpIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">GGR</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  {report.analytics.total.ggr > 0 ? (
                                    <ArrowTrendingUpIcon className="w-3 h-3 text-green-600" />
                                  ) : report.analytics.total.ggr < 0 ? (
                                    <ArrowTrendingDownIcon className="w-3 h-3 text-red-600" />
                                  ) : null}
                                  <span className={`text-lg font-bold ${
                                    report.analytics.total.ggr > 0 
                                      ? 'text-green-700 dark:text-green-300' 
                                      : report.analytics.total.ggr < 0
                                      ? 'text-red-700 dark:text-red-300'
                                      : 'text-gray-700 dark:text-gray-300'
                                  }`}>
                                    {formatCurrency(report.analytics.total.ggr, currency)}
                                  </span>
                                </div>
                              </div>
                            ) : (
                              // Expanded view - show all metrics
                              <div className="p-3 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700/50 dark:to-gray-800/50 rounded-xl">
                                <div className="grid grid-cols-3 gap-3">
                                  <div className="text-center space-y-1">
                                    <div className="flex items-center justify-center gap-1">
                                      <ArrowTrendingUpIcon className="w-3 h-3 text-green-600 dark:text-green-400" />
                                      <p className="text-xs font-medium text-gray-600 dark:text-gray-400">GGR</p>
                                    </div>
                                    <div className="flex items-center justify-center gap-1">
                                      {report.analytics.total.ggr > 0 ? (
                                        <ArrowTrendingUpIcon className="w-3 h-3 text-green-600" />
                                      ) : report.analytics.total.ggr < 0 ? (
                                        <ArrowTrendingDownIcon className="w-3 h-3 text-red-600" />
                                      ) : null}
                                      <p className={`text-sm lg:text-base font-bold break-all ${
                                        report.analytics.total.ggr > 0 
                                          ? 'text-green-700 dark:text-green-300' 
                                          : report.analytics.total.ggr < 0
                                          ? 'text-red-700 dark:text-red-300'
                                          : 'text-gray-700 dark:text-gray-300'
                                      }`}>
                                        {formatCurrency(report.analytics.total.ggr, currency)}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="text-center space-y-1 border-x border-gray-200 dark:border-gray-600">
                                    <div className="flex items-center justify-center gap-1">
                                      <CurrencyDollarIcon className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                                      <p className="text-xs font-medium text-gray-600 dark:text-gray-400">NGR</p>
                                    </div>
                                    <p className="text-sm lg:text-base font-bold text-blue-700 dark:text-blue-300 break-all">
                                      {formatCurrency(report.analytics.total.ngr, currency)}
                                    </p>
                                  </div>
                                  <div className="text-center space-y-1">
                                    <div className="flex items-center justify-center gap-1">
                                      <ChartBarIcon className="w-3 h-3 text-purple-600 dark:text-purple-400" />
                                      <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Turnover</p>
                                    </div>
                                    <p className="text-sm lg:text-base font-bold text-purple-700 dark:text-purple-300 break-all">
                                      {formatNumber(report.analytics.total.turnover)}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )
                          ) : (
                            <div className="p-8 text-center">
                              <div className="animate-pulse">
                                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mx-auto mb-2"></div>
                                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mx-auto"></div>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Expand Button - Compact */}
                        <div className="flex justify-center py-2 border-t border-gray-200 dark:border-gray-700">
                          <button
                            onClick={() => toggleReportExpanded(report.username)}
                            className="flex items-center gap-1.5 hover:opacity-80 transition-opacity duration-200 focus:outline-none"
                            title={isExpanded ? 'Hide details' : 'Show details'}
                          >
                            <ChevronDownIcon className={`w-4 h-4 text-gray-400 dark:text-gray-500 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                            <span className="text-xs text-gray-400 dark:text-gray-500">
                              {isExpanded ? 'Hide' : 'Details'}
                            </span>
                          </button>
                        </div>

                        {/* Expanded Content - Inside the card */}
                        {isExpanded && report.analytics && (
                          <div className="border-t border-gray-200 dark:border-gray-700 p-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Casino Section */}
                                <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg p-4">
                                  <div className="flex items-center justify-between mb-3">
                                    <h4 className="text-sm font-semibold text-purple-800 dark:text-purple-200 flex items-center gap-2">
                                      <span className="text-lg">🎰</span>
                                      Casino Performance
                                    </h4>
                                  </div>
                                  <div className="space-y-3">
                                    <div className="flex justify-between items-center py-2 border-b border-purple-200 dark:border-purple-800/30">
                                      <span className="text-sm text-gray-600 dark:text-gray-400">GGR</span>
                                      <span className="text-sm font-bold text-gray-900 dark:text-white">
                                        {formatCurrency(report.analytics.casino.ggr, currency)}
                                      </span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 border-b border-purple-200 dark:border-purple-800/30">
                                      <span className="text-sm text-gray-600 dark:text-gray-400">NGR</span>
                                      <span className="text-sm font-bold text-gray-900 dark:text-white">
                                        {formatCurrency(report.analytics.casino.ngr, currency)}
                                      </span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 border-b border-purple-200 dark:border-purple-800/30">
                                      <span className="text-sm text-gray-600 dark:text-gray-400">Turnover</span>
                                      <span className="text-sm font-bold text-gray-900 dark:text-white">
                                        {formatCurrency(report.analytics.casino.turnover, currency)}
                                      </span>
                                    </div>
                                    <div className="flex justify-between items-center py-2">
                                      <span className="text-sm text-gray-600 dark:text-gray-400">Win Rate</span>
                                      <span className="text-sm font-bold text-purple-600 dark:text-purple-400">
                                        {report.analytics.casino.turnover > 0 
                                          ? ((report.analytics.casino.ggr / report.analytics.casino.turnover) * 100).toFixed(2) 
                                          : '0.00'}%
                                      </span>
                                    </div>
                                  </div>
                                </div>

                                {/* Sport Section */}
                                <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg p-4">
                                  <div className="flex items-center justify-between mb-3">
                                    <h4 className="text-sm font-semibold text-green-800 dark:text-green-200 flex items-center gap-2">
                                      <span className="text-lg">⚽</span>
                                      Sport Performance
                                    </h4>
                                  </div>
                                  <div className="space-y-3">
                                    <div className="flex justify-between items-center py-2 border-b border-green-200 dark:border-green-800/30">
                                      <span className="text-sm text-gray-600 dark:text-gray-400">GGR</span>
                                      <span className="text-sm font-bold text-gray-900 dark:text-white">
                                        {formatCurrency(report.analytics.sport.ggr, currency)}
                                      </span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 border-b border-green-200 dark:border-green-800/30">
                                      <span className="text-sm text-gray-600 dark:text-gray-400">NGR</span>
                                      <span className="text-sm font-bold text-gray-900 dark:text-white">
                                        {formatCurrency(report.analytics.sport.ngr, currency)}
                                      </span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 border-b border-green-200 dark:border-green-800/30">
                                      <span className="text-sm text-gray-600 dark:text-gray-400">Turnover</span>
                                      <span className="text-sm font-bold text-gray-900 dark:text-white">
                                        {formatCurrency(report.analytics.sport.turnover, currency)}
                                      </span>
                                    </div>
                                    <div className="flex justify-between items-center py-2">
                                      <span className="text-sm text-gray-600 dark:text-gray-400">Win Rate</span>
                                      <span className="text-sm font-bold text-green-600 dark:text-green-400">
                                        {report.analytics.sport.turnover > 0 
                                          ? ((report.analytics.sport.ggr / report.analytics.sport.turnover) * 100).toFixed(2) 
                                          : '0.00'}%
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Total Summary */}
                              <div className="mt-4 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 rounded-lg p-3">
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Total Combined</span>
                                  <div className="flex items-center gap-4 text-sm">
                                    <span className="text-gray-600 dark:text-gray-400">
                                      GGR: <span className="font-bold text-gray-900 dark:text-white">{formatCurrency(report.analytics.total.ggr, currency)}</span>
                                    </span>
                                    <span className="text-gray-600 dark:text-gray-400">
                                      NGR: <span className="font-bold text-gray-900 dark:text-white">{formatCurrency(report.analytics.total.ngr, currency)}</span>
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}

      {/* GGR Analysis Modal */}
      {showGGRAnalysisModal && ggrAnalysisResults && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-yellow-500 to-orange-500 px-6 py-4 flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                  <ScaleIcon className="w-7 h-7" />
                  GGR Accuracy Analysis
                </h2>
                <p className="text-yellow-100 text-sm mt-1">
                  {ggrAnalysisResults.summary.selectedUser.name || ggrAnalysisResults.summary.selectedUser.username} • 
                  {formatInTimeZone(ggrAnalysisResults.summary.dateRange.startDate, BEIRUT_TIMEZONE, 'MMM d, yyyy')} - {formatInTimeZone(ggrAnalysisResults.summary.dateRange.endDate, BEIRUT_TIMEZONE, 'MMM d, yyyy')} • 
                  {ggrAnalysisResults.summary.currency}
                </p>
              </div>
              <button
                onClick={() => setShowGGRAnalysisModal(false)}
                className="text-white hover:text-yellow-100 transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            {/* Info Banner - Outside scrollable area */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-800 px-6 py-3">
              <div className="flex items-center gap-2 text-xs text-blue-900 dark:text-blue-100">
                <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span><strong>Formula:</strong> Expected = Start + Deposits - Withdrawals - GGR | Actual = Balance at end of selected period (not today)</span>
              </div>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Summary Section */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Summary</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white dark:bg-gray-700 rounded-lg p-4 shadow-sm">
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Players</div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                      {ggrAnalysisResults.summary.totalPlayers}
                    </div>
                  </div>
                  <div className="bg-white dark:bg-gray-700 rounded-lg p-4 shadow-sm">
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">With Mismatches</div>
                    <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                      {ggrAnalysisResults.summary.playersWithMismatches}
                    </div>
                  </div>
                  <div className="bg-white dark:bg-gray-700 rounded-lg p-4 shadow-sm">
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total GGR (Analytics)</div>
                    <div className="text-xl font-bold text-gray-900 dark:text-white">
                      {formatCurrency(ggrAnalysisResults.summary.totalAnalyticsGGR, ggrAnalysisResults.summary.currency)}
                    </div>
                  </div>
                  <div className="bg-white dark:bg-gray-700 rounded-lg p-4 shadow-sm">
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Net Wallet Flow</div>
                    <div className="text-xl font-bold text-gray-900 dark:text-white">
                      {formatCurrency(ggrAnalysisResults.summary.totalNetFlow, ggrAnalysisResults.summary.currency)}
                    </div>
                  </div>
                </div>
                
                {ggrAnalysisResults.summary.totalMismatchAmount > 0 && (
                  <div className="mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <span className="font-semibold text-red-900 dark:text-red-100">
                        Total Discrepancy: {formatCurrency(ggrAnalysisResults.summary.totalMismatchAmount, ggrAnalysisResults.summary.currency)}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Player Details */}
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                    Player Analysis ({ggrAnalysisResults.playerAnalysis.length} player{ggrAnalysisResults.playerAnalysis.length !== 1 ? 's' : ''})
                  </h3>
                  <button
                    onClick={() => {
                      if (expandedPlayers.size === ggrAnalysisResults.playerAnalysis.length) {
                        setExpandedPlayers(new Set());
                      } else {
                        setExpandedPlayers(new Set(ggrAnalysisResults.playerAnalysis.map(p => p.player)));
                      }
                    }}
                    className="text-sm px-3 py-1 rounded-lg bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors text-gray-700 dark:text-gray-300"
                  >
                    {expandedPlayers.size === ggrAnalysisResults.playerAnalysis.length ? 'Collapse All' : 'Expand All'}
                  </button>
                </div>
                <div className="space-y-2">
                  {ggrAnalysisResults.playerAnalysis.map((player) => {
                    const isExpanded = expandedPlayers.has(player.player);
                    const colors = getSeverityColor(player.mismatchPercent);
                    
                    return (
                      <div
                        key={player.player}
                        className={`rounded-lg border-2 ${colors.bg} ${colors.border} transition-all duration-200`}
                      >
                        {/* Collapsed Header - Always Visible */}
                        <button
                          onClick={() => togglePlayerExpanded(player.player)}
                          className="w-full p-4 text-left hover:opacity-80 transition-opacity"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3 flex-1">
                              <div className={`flex-shrink-0 w-10 h-10 rounded-full ${colors.badge} flex items-center justify-center`}>
                                {player.hasMismatch ? (
                                  <svg className={`w-5 h-5 ${colors.icon}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                  </svg>
                                ) : (
                                  <svg className={`w-5 h-5 ${colors.icon}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </div>
                              
                              <div className="flex-1 min-w-0">
                                <h4 className="font-bold text-gray-900 dark:text-white truncate">
                                  {player.playerName}
                                </h4>
                                <p className="text-xs text-gray-600 dark:text-gray-400">@{player.player}</p>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-3">
                              {/* Quick Stats */}
                              <div className="hidden sm:flex items-center gap-4 text-sm mr-4">
                                <div className="text-center">
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {player.analyticsGGR > 0 ? 'GGR Loss' : player.analyticsGGR < 0 ? 'GGR Win' : 'GGR'}
                                  </div>
                                  <div className={`font-bold ${
                                    player.analyticsGGR > 0 
                                      ? 'text-orange-600 dark:text-orange-400' 
                                      : player.analyticsGGR < 0
                                        ? 'text-green-600 dark:text-green-400'
                                        : 'text-gray-900 dark:text-white'
                                  }`}>
                                    {player.analyticsGGR > 0 ? '-' : player.analyticsGGR < 0 ? '+' : ''}
                                    {formatCurrency(Math.abs(player.analyticsGGR), ggrAnalysisResults.summary.currency)}
                                  </div>
                                </div>
                                {player.hasMismatch && (
                                  <div className="text-center">
                                    <div className="text-xs text-gray-500 dark:text-gray-400">Error %</div>
                                    <div className={`font-bold ${colors.text}`}>
                                      {player.mismatchPercent.toFixed(2)}%
                                    </div>
                                  </div>
                                )}
                              </div>
                              
                              {/* Status Badge */}
                              <div className={`px-3 py-1 rounded-full text-xs font-semibold ${colors.badge}`}>
                                {player.hasMismatch 
                                  ? player.mismatchPercent >= 100 
                                    ? 'CRITICAL' 
                                    : player.mismatchPercent >= 10
                                      ? 'HIGH'
                                      : player.mismatchPercent >= 5
                                        ? 'MEDIUM'
                                        : 'LOW'
                                  : 'MATCH'}
                              </div>
                              
                              {/* Expand/Collapse Icon */}
                              <ChevronDownIcon 
                                className={`w-5 h-5 text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                              />
                            </div>
                          </div>
                        </button>

                        {/* Expanded Details */}
                        {isExpanded && (
                          <div className="px-4 pb-4 border-t border-gray-200 dark:border-gray-700 pt-4">
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm mb-4">
                              <div>
                                <div className="text-gray-600 dark:text-gray-400">Analytics GGR</div>
                                <div className="font-bold text-gray-900 dark:text-white">
                                  {formatCurrency(player.analyticsGGR, ggrAnalysisResults.summary.currency)}
                                </div>
                              </div>
                              <div>
                                <div className="text-gray-600 dark:text-gray-400">Deposits</div>
                                <div className="font-bold text-green-600 dark:text-green-400">
                                  +{formatCurrency(player.totalDeposits, ggrAnalysisResults.summary.currency)}
                                </div>
                              </div>
                              <div>
                                <div className="text-gray-600 dark:text-gray-400">Withdrawals</div>
                                <div className="font-bold text-red-600 dark:text-red-400">
                                  -{formatCurrency(player.totalWithdrawals, ggrAnalysisResults.summary.currency)}
                                </div>
                              </div>
                              <div>
                                <div className="text-gray-600 dark:text-gray-400">Net Flow</div>
                                <div className="font-bold text-gray-900 dark:text-white">
                                  {formatCurrency(player.netFlow, ggrAnalysisResults.summary.currency)}
                                </div>
                              </div>
                              <div>
                                <div className="text-gray-600 dark:text-gray-400">Transaction Count</div>
                                <div className="font-bold text-gray-900 dark:text-white">
                                  {player.transactionCount}
                                </div>
                              </div>
                              <div>
                                <div className="text-gray-600 dark:text-gray-400">Analytics Records</div>
                                <div className="font-bold text-gray-900 dark:text-white">
                                  {player.analyticsRecordCount}
                                </div>
                              </div>
                            </div>

                            {/* Balance Reconciliation Section */}
                            {player.startingBalance !== null && (
                              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                                <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">Balance Reconciliation:</div>
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                  <div className="bg-white dark:bg-gray-800 rounded p-2">
                                    <div className="text-gray-500 dark:text-gray-400">Starting Balance</div>
                                    <div className="font-semibold text-gray-900 dark:text-white">
                                      {formatCurrency(player.startingBalance, ggrAnalysisResults.summary.currency)}
                                    </div>
                                  </div>
                                  <div className="bg-white dark:bg-gray-800 rounded p-2">
                                    <div className="text-gray-500 dark:text-gray-400">+ Deposits</div>
                                    <div className="font-semibold text-green-600 dark:text-green-400">
                                      +{formatCurrency(player.totalDeposits, ggrAnalysisResults.summary.currency)}
                                    </div>
                                  </div>
                                  <div className="bg-white dark:bg-gray-800 rounded p-2">
                                    <div className="text-gray-500 dark:text-gray-400">- Withdrawals</div>
                                    <div className="font-semibold text-red-600 dark:text-red-400">
                                      -{formatCurrency(player.totalWithdrawals, ggrAnalysisResults.summary.currency)}
                                    </div>
                                  </div>
                                  {player.analyticsGGR !== 0 && (
                                    <div className="bg-white dark:bg-gray-800 rounded p-2">
                                      <div className="text-gray-500 dark:text-gray-400">
                                        {player.analyticsGGR > 0 ? '- GGR (Lost)' : '+ GGR (Won)'}
                                      </div>
                                      <div className={`font-semibold ${
                                        player.analyticsGGR > 0 
                                          ? 'text-orange-600 dark:text-orange-400' 
                                          : 'text-green-600 dark:text-green-400'
                                      }`}>
                                        {player.analyticsGGR > 0 ? '-' : '+'}
                                        {formatCurrency(Math.abs(player.analyticsGGR), ggrAnalysisResults.summary.currency)}
                                      </div>
                                    </div>
                                  )}
                                  {player.analyticsGGR === 0 && (
                                    <div className="bg-white dark:bg-gray-800 rounded p-2">
                                      <div className="text-gray-500 dark:text-gray-400">GGR (None)</div>
                                      <div className="font-semibold text-gray-500 dark:text-gray-400">
                                        {formatCurrency(0, ggrAnalysisResults.summary.currency)}
                                      </div>
                                    </div>
                                  )}
                                </div>
                                <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                                  <div className={`rounded p-2 ${player.hasMismatch ? 'bg-yellow-50 dark:bg-yellow-900/20' : 'bg-white dark:bg-gray-800'}`}>
                                    <div className="text-gray-500 dark:text-gray-400">Expected Ending</div>
                                    <div className="font-bold text-gray-900 dark:text-white">
                                      {player.expectedEndingBalance !== null 
                                        ? formatCurrency(player.expectedEndingBalance, ggrAnalysisResults.summary.currency)
                                        : 'N/A'}
                                    </div>
                                  </div>
                                  <div className={`rounded p-2 ${player.hasMismatch ? 'bg-red-100 dark:bg-red-900/30' : 'bg-green-50 dark:bg-green-900/20'}`}>
                                    <div className="text-gray-500 dark:text-gray-400">Actual Ending</div>
                                    <div className={`font-bold ${player.hasMismatch ? 'text-red-700 dark:text-red-300' : 'text-green-700 dark:text-green-300'}`}>
                                      {player.actualEndingBalance !== null 
                                        ? formatCurrency(player.actualEndingBalance, ggrAnalysisResults.summary.currency)
                                        : 'N/A'}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}

                            {player.hasMismatch && (
                              <div className="mt-3 bg-red-100 dark:bg-red-900/30 rounded-lg p-3">
                                <div className="flex items-center justify-between gap-2">
                                  <div className="flex items-center gap-2">
                                    <svg className="w-4 h-4 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                    <span className="text-sm text-red-700 dark:text-red-300 font-semibold">
                                      {player.mismatchAmount > 0 ? '+' : ''}{formatCurrency(Math.abs(player.mismatchAmount), ggrAnalysisResults.summary.currency)} discrepancy
                                    </span>
                                  </div>
                                  <span className="text-xs text-red-600 dark:text-red-400">
                                    {player.mismatchPercent.toFixed(1)}% error
                                  </span>
                                </div>
                                
                                {/* Deep Dive Button */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeepDive(player.player);
                                  }}
                                  disabled={deepDiveLoading}
                                  className="mt-3 w-full py-2 px-4 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 disabled:from-gray-400 disabled:to-gray-400 text-white rounded-lg text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-2"
                                >
                                  {deepDiveLoading && deepDivePlayer?.player === player.player ? (
                                    <>
                                      <ArrowPathIcon className="w-4 h-4 animate-spin" />
                                      Analyzing...
                                    </>
                                  ) : (
                                    <>
                                      <MagnifyingGlassIcon className="w-4 h-4" />
                                      Deep Dive Analysis
                                    </>
                                  )}
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 dark:bg-gray-900 px-6 py-4 flex justify-between items-center border-t border-gray-200 dark:border-gray-700">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Analysis completed • {ggrAnalysisResults.playerAnalysis.length} player(s) analyzed
              </div>
              <button
                onClick={() => setShowGGRAnalysisModal(false)}
                className="px-4 py-2 bg-gray-800 dark:bg-gray-700 text-white rounded-lg hover:bg-gray-700 dark:hover:bg-gray-600 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deep Dive Analysis Modal */}
      {showDeepDiveModal && deepDiveResults && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[300] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4 flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                  <MagnifyingGlassIcon className="w-7 h-7" />
                  Deep Dive Analysis
                </h2>
                <p className="text-purple-100 text-sm mt-1">
                  {deepDiveResults.player.playerName} (@{deepDiveResults.player.player}) • 
                  {formatInTimeZone(deepDiveResults.dateRange.startDate, BEIRUT_TIMEZONE, 'MMM d, yyyy')} - {formatInTimeZone(deepDiveResults.dateRange.endDate, BEIRUT_TIMEZONE, 'MMM d, yyyy')} • 
                  {deepDiveResults.currency}
                </p>
              </div>
              <button
                onClick={() => setShowDeepDiveModal(false)}
                className="text-white hover:text-purple-100 transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Comparison Summary */}
              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-lg p-6 mb-6 border-2 border-purple-200 dark:border-purple-800">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">GGR Comparison</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="bg-white dark:bg-gray-700 rounded-lg p-4 shadow-sm">
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Analytics GGR</div>
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                      {formatCurrency(deepDiveResults.comparison.analyticsGGR, deepDiveResults.currency)}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">From analytics table</div>
                  </div>
                  <div className="bg-white dark:bg-gray-700 rounded-lg p-4 shadow-sm">
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Calculated GGR</div>
                    <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                      {formatCurrency(deepDiveResults.comparison.calculatedGGR, deepDiveResults.currency)}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">From {deepDiveResults.totalRounds} transactions</div>
                  </div>
                  <div className="bg-white dark:bg-gray-700 rounded-lg p-4 shadow-sm">
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Discrepancy</div>
                    <div className={`text-2xl font-bold ${
                      Math.abs(deepDiveResults.comparison.discrepancy) < 0.01 
                        ? 'text-green-600 dark:text-green-400'
                        : Math.abs(deepDiveResults.comparison.discrepancyPercent) < 5
                          ? 'text-yellow-600 dark:text-yellow-400'
                          : 'text-red-600 dark:text-red-400'
                    }`}>
                      {formatCurrency(Math.abs(deepDiveResults.comparison.discrepancy), deepDiveResults.currency)}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {deepDiveResults.comparison.discrepancyPercent.toFixed(2)}% difference
                    </div>
                  </div>
                </div>

                {/* Platform Breakdown */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white dark:bg-gray-700 rounded-lg p-4">
                    <h4 className="font-bold text-purple-700 dark:text-purple-300 mb-2 flex items-center gap-2">
                      <span className="text-lg">🎰</span> Casino
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Analytics:</span>
                        <span className="font-semibold">{formatCurrency(deepDiveResults.comparison.byPlatform.casino.analytics, deepDiveResults.currency)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Calculated:</span>
                        <span className="font-semibold">{formatCurrency(deepDiveResults.comparison.byPlatform.casino.calculated, deepDiveResults.currency)}</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-gray-200 dark:border-gray-600">
                        <span className="text-gray-600 dark:text-gray-400">Difference:</span>
                        <span className={`font-bold ${
                          Math.abs(deepDiveResults.comparison.byPlatform.casino.diff) < 0.01 
                            ? 'text-green-600 dark:text-green-400' 
                            : 'text-red-600 dark:text-red-400'
                        }`}>
                          {formatCurrency(Math.abs(deepDiveResults.comparison.byPlatform.casino.diff), deepDiveResults.currency)}
                          {Math.abs(deepDiveResults.comparison.byPlatform.casino.diff) >= 0.01 && 
                            ` (${deepDiveResults.comparison.byPlatform.casino.diffPercent.toFixed(1)}%)`
                          }
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {deepDiveResults.casinoRounds} rounds analyzed
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white dark:bg-gray-700 rounded-lg p-4">
                    <h4 className="font-bold text-green-700 dark:text-green-300 mb-2 flex items-center gap-2">
                      <span className="text-lg">⚽</span> Sport
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Analytics:</span>
                        <span className="font-semibold">{formatCurrency(deepDiveResults.comparison.byPlatform.sport.analytics, deepDiveResults.currency)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Calculated:</span>
                        <span className="font-semibold">{formatCurrency(deepDiveResults.comparison.byPlatform.sport.calculated, deepDiveResults.currency)}</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-gray-200 dark:border-gray-600">
                        <span className="text-gray-600 dark:text-gray-400">Difference:</span>
                        <span className={`font-bold ${
                          Math.abs(deepDiveResults.comparison.byPlatform.sport.diff) < 0.01 
                            ? 'text-green-600 dark:text-green-400' 
                            : 'text-red-600 dark:text-red-400'
                        }`}>
                          {formatCurrency(Math.abs(deepDiveResults.comparison.byPlatform.sport.diff), deepDiveResults.currency)}
                          {Math.abs(deepDiveResults.comparison.byPlatform.sport.diff) >= 0.01 && 
                            ` (${deepDiveResults.comparison.byPlatform.sport.diffPercent.toFixed(1)}%)`
                          }
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {deepDiveResults.sportRounds} bets analyzed
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Problematic Rounds */}
              {deepDiveResults.problematicRounds.length > 0 && (
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                      Problematic Rounds ({deepDiveResults.problematicRounds.length})
                    </h3>
                    <button
                      onClick={() => {
                        if (expandedRounds.size === deepDiveResults.problematicRounds.length) {
                          setExpandedRounds(new Set());
                        } else {
                          setExpandedRounds(new Set(deepDiveResults.problematicRounds.map(r => r.roundId)));
                        }
                      }}
                      className="text-sm px-3 py-1 rounded-lg bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors text-gray-700 dark:text-gray-300"
                    >
                      {expandedRounds.size === deepDiveResults.problematicRounds.length ? 'Collapse All' : 'Expand All'}
                    </button>
                  </div>
                  
                  <div className="space-y-2">
                    {deepDiveResults.problematicRounds.map((round) => {
                      const isExpanded = expandedRounds.has(round.roundId);
                      const hasCriticalIssues = round.issues.length > 0;
                      const hasWarnings = round.warnings.length > 0;
                      
                      return (
                        <div
                          key={round.roundId}
                          className={`rounded-lg border-2 ${
                            hasCriticalIssues 
                              ? 'bg-red-50 dark:bg-red-900/10 border-red-300 dark:border-red-700'
                              : hasWarnings
                                ? 'bg-orange-50 dark:bg-orange-900/10 border-orange-300 dark:border-orange-700'
                                : 'bg-yellow-50 dark:bg-yellow-900/10 border-yellow-300 dark:border-yellow-700'
                          }`}
                        >
                          {/* Round Header */}
                          <button
                            onClick={() => toggleRoundExpanded(round.roundId)}
                            className="w-full p-4 text-left hover:opacity-80 transition-opacity"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                <div className="flex-shrink-0">
                                  <span className="text-2xl">
                                    {round.isSportsBet ? '⚽' : '🎰'}
                                  </span>
                                </div>
                                
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <h4 className="font-bold text-gray-900 dark:text-white truncate">
                                      {round.isSportsBet ? 'Sport Bet' : 'Casino Round'}
                                    </h4>
                                    {hasCriticalIssues && (
                                      <span className="flex-shrink-0 px-2 py-0.5 bg-red-600 text-white text-xs rounded-full font-semibold">
                                        CRITICAL
                                      </span>
                                    )}
                                    {!hasCriticalIssues && hasWarnings && (
                                      <span className="flex-shrink-0 px-2 py-0.5 bg-orange-600 text-white text-xs rounded-full font-semibold">
                                        WARNING
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                                    ID: {round.roundId}
                                  </p>
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-4">
                                {/* Quick Stats */}
                                <div className="hidden md:flex items-center gap-3 text-sm">
                                  <div className="text-center">
                                    <div className="text-xs text-gray-500 dark:text-gray-400">Bet</div>
                                    <div className="font-bold text-gray-900 dark:text-white">
                                      {formatCurrency(round.totalBet, round.currency)}
                                    </div>
                                  </div>
                                  <div className="text-center">
                                    <div className="text-xs text-gray-500 dark:text-gray-400">Win</div>
                                    <div className="font-bold text-gray-900 dark:text-white">
                                      {formatCurrency(round.totalWin + round.totalSettle, round.currency)}
                                    </div>
                                  </div>
                                  <div className="text-center">
                                    <div className="text-xs text-gray-500 dark:text-gray-400">GGR</div>
                                    <div className={`font-bold ${
                                      round.roundGGR > 0 
                                        ? 'text-orange-600 dark:text-orange-400' 
                                        : round.roundGGR < 0
                                          ? 'text-green-600 dark:text-green-400'
                                          : 'text-gray-900 dark:text-white'
                                    }`}>
                                      {round.roundGGR > 0 ? '-' : round.roundGGR < 0 ? '+' : ''}
                                      {formatCurrency(Math.abs(round.roundGGR), round.currency)}
                                    </div>
                                  </div>
                                </div>
                                
                                <ChevronDownIcon 
                                  className={`w-5 h-5 text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                />
                              </div>
                            </div>
                          </button>

                          {/* Expanded Round Details */}
                          {isExpanded && (
                            <div className="px-4 pb-4 border-t border-gray-200 dark:border-gray-700 pt-4">
                              {/* Issues/Warnings */}
                              {(round.issues.length > 0 || round.warnings.length > 0 || round.info.length > 0) && (
                                <div className="mb-4 space-y-2">
                                  {round.issues.map((issue, idx) => (
                                    <div key={idx} className="bg-red-100 dark:bg-red-900/30 rounded-lg p-3 text-sm">
                                      <div className="flex items-center gap-2 mb-1">
                                        <svg className="w-4 h-4 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        </svg>
                                        <span className="font-semibold text-red-700 dark:text-red-300">{issue.message}</span>
                                      </div>
                                      <div className="text-xs text-red-600 dark:text-red-400">Impact: {issue.impact}</div>
                                    </div>
                                  ))}
                                  
                                  {round.warnings.map((warning, idx) => (
                                    <div key={idx} className="bg-orange-100 dark:bg-orange-900/30 rounded-lg p-3 text-sm">
                                      <div className="flex items-center gap-2 mb-1">
                                        <svg className="w-4 h-4 text-orange-600 dark:text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        </svg>
                                        <span className="font-semibold text-orange-700 dark:text-orange-300">{warning.message}</span>
                                      </div>
                                      <div className="text-xs text-orange-600 dark:text-orange-400">Impact: {warning.impact}</div>
                                    </div>
                                  ))}
                                  
                                  {round.info.map((info, idx) => (
                                    <div key={idx} className="bg-blue-100 dark:bg-blue-900/30 rounded-lg p-3 text-sm">
                                      <div className="flex items-center gap-2 mb-1">
                                        <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <span className="font-semibold text-blue-700 dark:text-blue-300">{info.message}</span>
                                      </div>
                                      <div className="text-xs text-blue-600 dark:text-blue-400">Impact: {info.impact}</div>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Round Statistics */}
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-4">
                                <div className="bg-white dark:bg-gray-800 rounded p-2">
                                  <div className="text-gray-500 dark:text-gray-400 text-xs">Total Bet</div>
                                  <div className="font-bold text-blue-600 dark:text-blue-400">
                                    {formatCurrency(round.totalBet, round.currency)}
                                  </div>
                                </div>
                                <div className="bg-white dark:bg-gray-800 rounded p-2">
                                  <div className="text-gray-500 dark:text-gray-400 text-xs">Total Win</div>
                                  <div className="font-bold text-green-600 dark:text-green-400">
                                    {formatCurrency(round.totalWin + round.totalSettle, round.currency)}
                                  </div>
                                </div>
                                <div className="bg-white dark:bg-gray-800 rounded p-2">
                                  <div className="text-gray-500 dark:text-gray-400 text-xs">Net Result</div>
                                  <div className={`font-bold ${
                                    round.netResult > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                                  }`}>
                                    {round.netResult > 0 ? '+' : ''}{formatCurrency(round.netResult, round.currency)}
                                  </div>
                                </div>
                                <div className="bg-white dark:bg-gray-800 rounded p-2">
                                  <div className="text-gray-500 dark:text-gray-400 text-xs">Round GGR</div>
                                  <div className={`font-bold ${
                                    round.roundGGR > 0 
                                      ? 'text-orange-600 dark:text-orange-400' 
                                      : round.roundGGR < 0
                                        ? 'text-green-600 dark:text-green-400'
                                        : 'text-gray-900 dark:text-white'
                                  }`}>
                                    {formatCurrency(round.roundGGR, round.currency)}
                                  </div>
                                </div>
                              </div>

                              {/* Transaction List */}
                              <div className="bg-white dark:bg-gray-700 rounded-lg p-3">
                                <h5 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                  Transactions ({round.transactions.length})
                                </h5>
                                <div className="space-y-1 max-h-40 overflow-y-auto">
                                  {round.transactions.map((tx, idx) => (
                                    <div key={idx} className="flex justify-between items-center text-xs py-1 border-b border-gray-100 dark:border-gray-600 last:border-0">
                                      <div className="flex items-center gap-2">
                                        <span className={`font-medium ${
                                          tx.type?.toLowerCase() === 'withdrawal' ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'
                                        }`}>
                                          {tx.type}
                                        </span>
                                        {tx.context?.reason && (
                                          <span className="text-gray-500 dark:text-gray-400">
                                            ({tx.context.reason})
                                          </span>
                                        )}
                                      </div>
                                      <span className="font-semibold text-gray-900 dark:text-white">
                                        {formatCurrency(parseFloat(tx.amount || 0), tx.currency)}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Summary Message */}
              {Math.abs(deepDiveResults.comparison.discrepancy) >= 0.01 && (
                <div className={`border rounded-lg p-3 ${
                  Math.abs(deepDiveResults.comparison.discrepancy) < 0.01 
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                    : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                }`}>
                  <div className="flex items-center gap-2 text-sm">
                    <span className={Math.abs(deepDiveResults.comparison.discrepancy) < 0.01 ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}>
                      {deepDiveResults.comparison.discrepancy > 0 ? '⚠️ Analytics higher' : '⚠️ Calculated higher'} by {formatCurrency(Math.abs(deepDiveResults.comparison.discrepancy), deepDiveResults.currency)}
                      {' - '}
                      {Math.abs(deepDiveResults.comparison.byPlatform.casino.diff) > Math.abs(deepDiveResults.comparison.byPlatform.sport.diff)
                        ? 'Casino issue'
                        : 'Sport issue'}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 dark:bg-gray-900 px-6 py-4 flex justify-between items-center border-t border-gray-200 dark:border-gray-700">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Deep dive completed • {deepDiveResults.totalRounds} rounds/bets analyzed
              </div>
              <button
                onClick={() => setShowDeepDiveModal(false)}
                className="px-4 py-2 bg-gray-800 dark:bg-gray-700 text-white rounded-lg hover:bg-gray-700 dark:hover:bg-gray-600 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports; 